class ProductMedia extends HTMLElement {
  constructor() {
    super();

    this.selectors = {
      slider: "[data-slider]",
      mediaItem: "[data-media-id]",
      modalOpeners: "[data-modal-opener-id]",
      zoomImages: "[data-pswp-image]",
      btnXr: "[data-shopify-xr]",
    };

    this.selectedMediaIndex =
      Number(this.querySelector(this.selectors.slider).querySelector("[data-selected]")?.dataset.index) || 0;

    const swiperElement = document.getElementById("product__media-slider");
    let swiperOptions = {};
    try {
      const optionsString = this.querySelector(this.selectors.slider).getAttribute("data-swiper-options");
      swiperOptions = optionsString ? JSON.parse(optionsString) : {};
    } catch (error) {
      console.error("Invalid JSON in data-swiper-options:", error);
    }

    const withThumbs = this.dataset.withThumbs === "true";

    // Flag to distinguish between user clicks and programmatic changes
    this.isUserInitiatedChange = false;

    this.settings = {
      sliderElement: this.querySelector(this.selectors.slider),
      sliderInstance: null,
      options: {
        ...swiperOptions,
        initialSlide: this.selectedMediaIndex,
        navigation: {
          prevEl: this.querySelector(".swiper-button-prev"),
          nextEl: this.querySelector(".swiper-button-next"),
        },
        pagination: {
          el: ".swiper-pagination",
          type: "progressbar",
        },
        spaceBetween: 2,
        watchOverflow: true,
        watchSlidesVisibility: true,
        watchSlidesProgress: true,
        preventInteractionOnTransition: false,
        autoHeight: this.dataset.autoHeight === "true",
        loop: true,
        allowTouchMove: true,
        createElements: true,
        speed: 600,
        effect: "fade",
        fadeEffect: {
          crossFade: true,
        },
        breakpoints: {
          750: {
            navigation: {
              nextEl: ".swiper-button-next",
              prevEl: ".swiper-button-prev",
            },
          },
        },
        on: {
          afterInit: (swiper) => this.afterInit(swiper),
          slideChangeTransitionEnd: (swiper) => this.setActiveModalOpener(swiper),
          transitionEnd: (swiper) => this.playActiveSlideVideo(swiper),
          slideChange: (swiper) => this.syncThumbnails(swiper),
        },
      },
    };

    if (withThumbs) {
      this.initThumbnails(swiperElement);
    } else {
      this.settings.options.slidesPerView = 1;
    }
  }

  afterInit(swiper) {
    if (swiper.slides.length > 1) {
      this.playActiveSlideVideo(swiper);
    }
  }

  pauseAllVideos(swiper) {
    swiper.el.querySelectorAll("video").forEach((video) => video.pause());
  }

  playActiveSlideVideo(swiper) {
    const activeSlide = swiper.slides[swiper.activeIndex];
    if (!activeSlide) return;
    const video = activeSlide.querySelector("video");
    if (video) this.playPromise(video);
  }

  playPromise(video) {
    let playPromise = video.play();
    if (playPromise !== undefined) {
      playPromise.catch(() => video.pause());
    }
  }

  initThumbnails(swiperElement) {
    const slideCount = this.querySelectorAll("[data-media-id]").length;

    this.thumbSwiper = new Swiper(".swiper-thumbs", {
      initialSlide: this.selectedMediaIndex,
      loopedSlides: slideCount,
      direction: "horizontal",
      centeredSlides: false,
      slidesPerView: 3.5,
      spaceBetween: 8,
      centeredSlides: false,
      freeMode: false,
      watchSlidesProgress: true,
      watchSlidesVisibility: true,
      allowTouchMove: true,
      threshold: 5,
      breakpoints: {
        750: {
          loop: true,
          spaceBetween: 8,
          slidesPerView: "auto",
          centeredSlides: false,
          slideToClickedSlide: false,
          direction: "vertical",
        },
      },
    });

    this.settings.options.thumbs = {
      swiper: this.thumbSwiper,
      slideThumbActiveClass: "swiper-slide-thumb-active",
      autoScrollOffset: 0,
      multipleActiveThumbs: false,
    };

    // Handle thumbnail clicks manually
    this.thumbSwiper.on("click", (swiper, event) => {
      const clickedSlide = event.target.closest(".swiper-slide");
      if (!clickedSlide) return;

      const clickedMedia = clickedSlide.querySelector("[data-media-id]");
      if (!clickedMedia) return;

      const targetMediaId = clickedMedia.dataset.mediaId;

      // Set flag to indicate this is a user-initiated change
      this.isUserInitiatedChange = true;

      // Find matching slide in main slider
      const mainSlides = this.settings.sliderInstance.slides;
      for (let i = 0; i < mainSlides.length; i++) {
        const mainSlide = mainSlides[i];
        if (mainSlide.dataset.mediaId === targetMediaId) {
          this.settings.sliderInstance.slideTo(i);
          break;
        }
      }
    });
  }

  updateThumbnailActiveState(mainSwiper) {
    const activeSlide = mainSwiper.slides[mainSwiper.activeIndex];
    if (!activeSlide) return;

    const activeMediaId = activeSlide.dataset.mediaId;

    // Remove active class from all thumbnails
    this.thumbSwiper.slides.forEach((thumbSlide) => {
      thumbSlide.classList.remove("swiper-slide-thumb-active");
    });

    // Add active class to matching thumbnail
    this.thumbSwiper.slides.forEach((thumbSlide) => {
      const thumbMedia = thumbSlide.querySelector("[data-media-id]");
      if (thumbMedia && thumbMedia.dataset.mediaId === activeMediaId) {
        thumbSlide.classList.add("swiper-slide-thumb-active");
      }
    });
  }

  // Check if a slide is currently visible in the thumbnail swiper
  isThumbSlideVisible(slideIndex) {
    if (!this.thumbSwiper) return false;

    const slide = this.thumbSwiper.slides[slideIndex];
    if (!slide) return false;

    const swiperContainer = this.thumbSwiper.el;
    const slideRect = slide.getBoundingClientRect();
    const containerRect = swiperContainer.getBoundingClientRect();

    // Check if slide is at least partially visible
    return (
      slideRect.left < containerRect.right &&
      slideRect.right > containerRect.left &&
      slideRect.top < containerRect.bottom &&
      slideRect.bottom > containerRect.top
    );
  }

  // Improved syncThumbnails method - prevent ALL scrolling during navigation

  syncThumbnails(mainSwiper) {
    if (!this.thumbSwiper) return;

    const realIndex = mainSwiper.realIndex;

    if (this.isUserInitiatedChange) {
      this.isUserInitiatedChange = false;
      return;
    }

    const activeThumbIndex = this.getThumbIndexByMediaId(mainSwiper.slides[mainSwiper.activeIndex].dataset.mediaId);

    if (activeThumbIndex !== -1 && !this.isThumbSlideVisible(activeThumbIndex)) {
      //scrolling here if the active thumbnail is not visible
      this.thumbSwiper.slideToLoop(realIndex, 300);
    }
  }

  getThumbIndexByMediaId(mediaId) {
    for (let i = 0; i < this.thumbSwiper.slides.length; i++) {
      const thumbMedia = this.thumbSwiper.slides[i].querySelector("[data-media-id]");
      if (thumbMedia && thumbMedia.dataset.mediaId === mediaId) {
        return i;
      }
    }
    return -1;
  }

  connectedCallback() {
    this.init();
  }

  init() {
    if (!this.settings.sliderElement) return;
    this.settings.sliderInstance = new Swiper(this.settings.sliderElement, this.settings.options);
  }

  setActiveModalOpener(swiper) {
    const activeSlide = swiper.slides[swiper.activeIndex];
    if (!activeSlide) return;
    const activeMediaId = activeSlide.dataset.mediaId;
    this.querySelector(`${this.selectors.modalOpeners}.is-active`)?.classList.remove("is-active");
    this.querySelector(
      `${this.selectors.modalOpeners} [data-media-id="${activeMediaId}"]`,
    )?.parentElement.classList.add("is-active");
    if (activeSlide.dataset.mediaType === "model") {
      this.querySelector(this.selectors.btnXr).dataset.shopifyModel3dId = activeMediaId;
    }
  }

  setActiveMedia(mediaId) {
    const swiper = this.settings.sliderInstance;
    if (!swiper) return;
    const mediaIdStr = String(mediaId);
    for (let i = 0; i < swiper.slides.length; i++) {
      const slide = swiper.slides[i];
      if (slide.dataset.mediaId === mediaIdStr) {
        const realIndex = parseInt(slide.dataset.swiperSlideIndex);
        swiper.slideToLoop(isNaN(realIndex) ? i : realIndex);
        return;
      }
    }
  }
}

customElements.define("product-media", ProductMedia);

// class ProductMedia extends HTMLElement {
//   constructor() {
//     super();

//     this.selectors = {
//       slider: "[data-slider]",
//       mediaItem: "[data-media-id]",
//       modalOpeners: "[data-modal-opener-id]",
//       zoomImages: "[data-pswp-image]",
//       btnXr: "[data-shopify-xr]",
//     };
//     this.selectedMediaIndex =
//       Number(
//         this.querySelector(this.selectors.slider).querySelector(
//           "[data-selected]"
//         )?.dataset.index
//       ) || 0;

//     const swiperElement = document.getElementById("product__media-slider");
//     let swiperOptions = {};
//     try {
//       const optionsString = this.querySelector(
//         this.selectors.slider
//       ).getAttribute("data-swiper-options");
//       swiperOptions = optionsString ? JSON.parse(optionsString) : {};
//     } catch (error) {
//       console.error("Invalid JSON in data-swiper-options:", error);
//     }

//     const withThumbs = this.dataset.withThumbs === "true";

//     this.settings = {
//       sliderElement: this.querySelector(this.selectors.slider),
//       sliderInstance: null,
//       options: {
//         ...swiperOptions,
//         initialSlide: this.selectedMediaIndex,
//         navigation: {
//           prevEl: this.querySelector(".swiper-button-prev"),
//           nextEl: this.querySelector(".swiper-button-next"),
//         },
//         pagination: {
//           el: ".swiper-pagination",
//           type: "progressbar",
//         },
//         spaceBetween: 2,
//         watchOverflow: true,
//         watchSlidesVisibility: true,
//         watchSlidesProgress: true,
//         preventInteractionOnTransition: false,
//         autoHeight: this.dataset.autoHeight === "true",
//         loop: true,
//         allowTouchMove: true,
//         createElements: true,
//         speed: 600,
//         effect: "fade",
//         fadeEffect: {
//           crossFade: true,
//         },
//         breakpoints: {
//           750: {
//             navigation: {
//               nextEl: ".swiper-button-next",
//               prevEl: ".swiper-button-prev",
//             },
//           },
//         },
//         on: {
//           afterInit: (swiper) => this.afterInit(swiper),
//           slideChangeTransitionEnd: (swiper) =>
//             this.setActiveModalOpener(swiper),
//           transitionEnd: (swiper) => this.playActiveSlideVideo(swiper),
//           slideChange: (swiper) => this.syncThumbnails(swiper),
//         },
//       },
//     };

//     if (withThumbs) {
//       this.initThumbnails(swiperElement);
//     } else {
//       this.settings.options.slidesPerView = 1;
//     }
//   }

//   afterInit(swiper) {
//     if (swiper.slides.length > 1) {
//       this.playActiveSlideVideo(swiper);
//     }
//   }

//   pauseAllVideos(swiper) {
//     swiper.el.querySelectorAll("video").forEach((video) => video.pause());
//   }

//   playActiveSlideVideo(swiper) {
//     const activeSlide = swiper.slides[swiper.activeIndex];
//     if (!activeSlide) return;
//     const video = activeSlide.querySelector("video");
//     if (video) this.playPromise(video);
//   }

//   playPromise(video) {
//     let playPromise = video.play();
//     if (playPromise !== undefined) {
//       playPromise.catch(() => video.pause());
//     }
//   }

//   initThumbnails(swiperElement) {
//     const slideCount = this.querySelectorAll("[data-media-id]").length;

//   // initThumbnails(swiperElement) {
//   //   const thumbSwiper = new Swiper(".swiper-thumbs", {
//   //     initialSlide: this.selectedMediaIndex,
//   //     centeredSlides: false,
//   //     centeredSlidesBounds: true,
//   //     freeMode: false,
//   //     direction: "horizontal",
//   //     slidesPerView: 3.5,
//   //     spaceBetween: 8,
//   //     threshold: 5,
//   //     watchOverflow: true,
//   //     watchSlidesVisibility: true,
//   //     watchSlidesProgress: true,
//   //     breakpoints: {
//   //       750: {
//   //         direction: swiperElement.dataset.thumbsDirectionDesktop || "vertical",
//   //         spaceBetween: 8,
//   //         slidesPerView: 3.5,
//   //       },
//   //     },
//   //   });

//     this.thumbSwiper = new Swiper(".swiper-thumbs", {
//       initialSlide: this.selectedMediaIndex,
//       loopedSlides: slideCount,
//       direction: "horizontal",
//        centeredSlides: false,
//       slidesPerView: 3.5,
//       spaceBetween: 8,
//       centeredSlides: false,
//       freeMode: false,
//       watchSlidesProgress: true,
//       watchSlidesVisibility: true,
//       allowTouchMove: true,
//       threshold:5,
//       breakpoints: {
//         750: {
//           loop:true,
//           spaceBetween: 12,
//           slidesPerView: "auto",
//           centeredSlides: false,
//           slideToClickedSlide: false,
//           direction: "vertical",
//         },
//       },
//     });

//     // Link main slider to thumbnails
//     // this.settings.options.loopedSlides = slideCount;
//     this.settings.options.thumbs = {
//       swiper: this.thumbSwiper,
//       slideThumbActiveClass: "swiper-slide-thumb-active",
//       autoScrollOffset: 0,
//       multipleActiveThumbs: false,
//     };

//     // Handle thumbnail clicks manually to avoid loop index conflicts
//     this.thumbSwiper.on("click", (swiper, event) => {
//       const clickedSlide = event.target.closest(".swiper-slide");
//       if (!clickedSlide) return;

//       // Get the media ID from the clicked thumbnail
//       const clickedMedia = clickedSlide.querySelector("[data-media-id]");
//       if (!clickedMedia) return;

//       const targetMediaId = clickedMedia.dataset.mediaId;

//       // Find matching slide in main slider
//       const mainSlides = this.settings.sliderInstance.slides;
//       for (let i = 0; i < mainSlides.length; i++) {
//         const mainSlide = mainSlides[i];
//         if (mainSlide.dataset.mediaId === targetMediaId) {
//           this.settings.sliderInstance.slideTo(i);
//           break;
//         }
//       }
//     });
//   }

//   updateThumbnailActiveState(mainSwiper) {
//     const activeSlide = mainSwiper.slides[mainSwiper.activeIndex];
//     if (!activeSlide) return;

//     const activeMediaId = activeSlide.dataset.mediaId;

//     this.thumbSwiper.slides.forEach((thumbSlide) => {
//       const thumbMedia = thumbSlide.querySelector("[data-media-id]");

//       if (thumbMedia && thumbMedia.dataset.mediaId === activeMediaId) {
//         thumbSlide.classList.add("swiper-slide-thumb-active");
//       }
//     });
//   }
//   syncThumbnails(mainSwiper) {
//     if (!this.thumbSwiper) return;

//     const realIndex = mainSwiper.realIndex;
//     this.updateThumbnailActiveState(mainSwiper);

//     this.thumbSwiper.slideToLoop(realIndex, 300);
//   }

//   connectedCallback() {
//     this.init();
//   }

//   init() {
//     if (!this.settings.sliderElement) return;
//     this.settings.sliderInstance = new Swiper(
//       this.settings.sliderElement,
//       this.settings.options
//     );
//   }

//   setActiveModalOpener(swiper) {
//     const activeSlide = swiper.slides[swiper.activeIndex];
//     if (!activeSlide) return;
//     const activeMediaId = activeSlide.dataset.mediaId;
//     this.querySelector(
//       `${this.selectors.modalOpeners}.is-active`
//     )?.classList.remove("is-active");
//     this.querySelector(
//       `${this.selectors.modalOpeners} [data-media-id="${activeMediaId}"]`
//     )?.parentElement.classList.add("is-active");
//     if (activeSlide.dataset.mediaType === "model") {
//       this.querySelector(this.selectors.btnXr).dataset.shopifyModel3dId =
//         activeMediaId;
//     }
//   }
// }

// customElements.define("product-media", ProductMedia);

//   class ProductMedia extends HTMLElement {
//             constructor() {
//                 super();
//
//                 this.selectors = {
//                     slider: "[data-slider]",
//                     mediaItem: "[data-media-id]",
//                     modalOpeners: "[data-modal-opener-id]",
//                     zoomImages: "[data-pswp-image]",
//                     btnXr: "[data-shopify-xr]",
//                 };
//
//                 this.selectedMediaIndex =
//                     Number(
//                         this.querySelector(this.selectors.slider).querySelector(
//                             "[data-selected]"
//                         )?.dataset.index
//                     ) || 0;
//
//                 const swiperElement = document.getElementById("product__media-slider");
//                 let swiperOptions = {};
//                 try {
//                     const optionsString = this.querySelector(
//                         this.selectors.slider
//                     ).getAttribute("data-swiper-options");
//                     swiperOptions = optionsString ? JSON.parse(optionsString) : {};
//                 } catch (error) {
//                     console.error("Invalid JSON in data-swiper-options:", error);
//                 }
//
//                 const withThumbs = this.dataset.withThumbs === "true";
//
//                 // Flag to distinguish between user clicks and programmatic changes
//                 this.isUserInitiatedChange = false;
//
//                 this.settings = {
//                     sliderElement: this.querySelector(this.selectors.slider),
//                     sliderInstance: null,
//                     options: {
//                         ...swiperOptions,
//                         initialSlide: this.selectedMediaIndex,
//                         navigation: {
//                             prevEl: this.querySelector(".swiper-button-prev"),
//                             nextEl: this.querySelector(".swiper-button-next"),
//                         },
//                         pagination: {
//                             el: ".swiper-pagination",
//                             type: "progressbar",
//                         },
//                         spaceBetween: 2,
//                         watchOverflow: true,
//                         watchSlidesVisibility: true,
//                         watchSlidesProgress: true,
//                         preventInteractionOnTransition: false,
//                         autoHeight: this.dataset.autoHeight === "true",
//                         loop: true,
//                         allowTouchMove: true,
//                         createElements: true,
//                         speed: 600,
//                         effect: "fade",
//                         fadeEffect: {
//                             crossFade: true,
//                         },
//                         breakpoints: {
//                             750: {
//                                 navigation: {
//                                     nextEl: ".swiper-button-next",
//                                     prevEl: ".swiper-button-prev",
//                                 },
//                             },
//                         },
//                         on: {
//                             afterInit: (swiper) => this.afterInit(swiper),
//                             slideChangeTransitionEnd: (swiper) =>
//                                 this.setActiveModalOpener(swiper),
//                             transitionEnd: (swiper) => this.playActiveSlideVideo(swiper),
//                             slideChange: (swiper) => this.syncThumbnails(swiper),
//                         },
//                     },
//                 };
//
//                 if (withThumbs) {
//                     this.initThumbnails(swiperElement);
//                 } else {
//                     this.settings.options.slidesPerView = 1;
//                 }
//             }
//
//             afterInit(swiper) {
//                 if (swiper.slides.length > 1) {
//                     this.playActiveSlideVideo(swiper);
//                 }
//             }
//
//             pauseAllVideos(swiper) {
//                 swiper.el.querySelectorAll("video").forEach((video) => video.pause());
//             }
//
//             playActiveSlideVideo(swiper) {
//                 const activeSlide = swiper.slides[swiper.activeIndex];
//                 if (!activeSlide) return;
//                 const video = activeSlide.querySelector("video");
//                 if (video) this.playPromise(video);
//             }
//
//             playPromise(video) {
//                 let playPromise = video.play();
//                 if (playPromise !== undefined) {
//                     playPromise.catch(() => video.pause());
//                 }
//             }
//
//             initThumbnails(swiperElement) {
//                 const slideCount = this.querySelectorAll("[data-media-id]").length;
//
//                 this.thumbSwiper = new Swiper(".swiper-thumbs", {
//                     initialSlide: this.selectedMediaIndex,
//                     loopedSlides: slideCount,
//                     direction: "horizontal",
//                     centeredSlides: false,
//                     slidesPerView: 3.5,
//                     spaceBetween: 8,
//                     centeredSlides: false,
//                     freeMode: false,
//                     watchSlidesProgress: true,
//                     watchSlidesVisibility: true,
//                     allowTouchMove: true,
//                     threshold: 5,
//                     breakpoints: {
//                         750: {
//                             loop: true,
//                             spaceBetween: 8,
//                             slidesPerView: "auto",
//                             centeredSlides: false,
//                             slideToClickedSlide: false,
//                             direction: "vertical",
//                         },
//                     },
//                 });
//
//                 this.settings.options.thumbs = {
//                     swiper: this.thumbSwiper,
//                     slideThumbActiveClass: "swiper-slide-thumb-active",
//                     autoScrollOffset: 0,
//                     multipleActiveThumbs: false,
//                 };
//
//                 // Handle thumbnail clicks manually
//                 this.thumbSwiper.on("click", (swiper, event) => {
//                     const clickedSlide = event.target.closest(".swiper-slide");
//                     if (!clickedSlide) return;
//
//                     const clickedMedia = clickedSlide.querySelector("[data-media-id]");
//                     if (!clickedMedia) return;
//
//                     const targetMediaId = clickedMedia.dataset.mediaId;
//
//                     // Set flag to indicate this is a user-initiated change
//                     this.isUserInitiatedChange = true;
//
//                     // Find matching slide in main slider
//                     const mainSlides = this.settings.sliderInstance.slides;
//                     for (let i = 0; i < mainSlides.length; i++) {
//                         const mainSlide = mainSlides[i];
//                         if (mainSlide.dataset.mediaId === targetMediaId) {
//                             this.settings.sliderInstance.slideTo(i);
//                             break;
//                         }
//                     }
//                 });
//             }
//
//             updateThumbnailActiveState(mainSwiper) {
//                 const activeSlide = mainSwiper.slides[mainSwiper.activeIndex];
//                 if (!activeSlide) return;
//
//                 const activeMediaId = activeSlide.dataset.mediaId;
//
//                 // Remove active class from all thumbnails
//                 this.thumbSwiper.slides.forEach((thumbSlide) => {
//                     thumbSlide.classList.remove("swiper-slide-thumb-active");
//                 });
//
//                 // Add active class to matching thumbnail
//                 this.thumbSwiper.slides.forEach((thumbSlide) => {
//                     const thumbMedia = thumbSlide.querySelector("[data-media-id]");
//                     if (thumbMedia && thumbMedia.dataset.mediaId === activeMediaId) {
//                         thumbSlide.classList.add("swiper-slide-thumb-active");
//                     }
//                 });
//             }
//
//             // Check if a slide is currently visible in the thumbnail swiper
//             isThumbSlideVisible(slideIndex) {
//                 if (!this.thumbSwiper) return false;
//
//                 const slide = this.thumbSwiper.slides[slideIndex];
//                 if (!slide) return false;
//
//                 const swiperContainer = this.thumbSwiper.el;
//                 const slideRect = slide.getBoundingClientRect();
//                 const containerRect = swiperContainer.getBoundingClientRect();
//
//                 // Check if slide is at least partially visible
//                 return (
//                     slideRect.left < containerRect.right &&
//                     slideRect.right > containerRect.left &&
//                     slideRect.top < containerRect.bottom &&
//                     slideRect.bottom > containerRect.top
//                 );
//             }
//
//     // Improved syncThumbnails method - prevent ALL scrolling during navigation
// // syncThumbnails(mainSwiper) {
// //     if (!this.thumbSwiper) return;
//
// //     // If this was a user-initiated change (thumbnail click), don't scroll
// //     if (this.isUserInitiatedChange) {
// //         this.isUserInitiatedChange = false;
// //         return;
// //     }
//
//
// // }
//
//    syncThumbnails(mainSwiper) {
//                 if (!this.thumbSwiper) return;
//
//                 const realIndex = mainSwiper.realIndex;
//
//                 if (this.isUserInitiatedChange) {
//                     this.isUserInitiatedChange = false;
//                     return;
//                 }
//
//
//                 const activeThumbIndex = this.getThumbIndexByMediaId(mainSwiper.slides[mainSwiper.activeIndex].dataset.mediaId);
//
//                 if (activeThumbIndex !== -1 && !this.isThumbSlideVisible(activeThumbIndex)) {
//                     // Only scroll if the active thumbnail is not visible
//                     this.thumbSwiper.slideToLoop(realIndex, 300);
//                 }
//     }
//
//   const activeThumbIndex = this.getThumbIndexByMediaId(mainSwiper.slides[mainSwiper.activeIndex].dataset.mediaId);
//
// // syncThumbnails(mainSwiper) {
// //     if (!this.thumbSwiper) return;
//
// //     // If this was a user-initiated change (thumbnail click), don't update anything
// //     if (this.isUserInitiatedChange) {
// //         this.isUserInitiatedChange = false;
// //         return;
// //     }
//
// //     // Manually update active state without any scrolling
// //     const activeSlide = mainSwiper.slides[mainSwiper.activeIndex];
// //     if (!activeSlide) return;
//
// //     const activeMediaId = activeSlide.dataset.mediaId;
//
// //     // Remove active class from all thumbnails
// //     this.thumbSwiper.slides.forEach((thumbSlide) => {
// //         thumbSlide.classList.remove("swiper-slide-thumb-active");
// //     });
//
// //     // Add active class to matching thumbnail
// //     this.thumbSwiper.slides.forEach((thumbSlide) => {
// //         const thumbMedia = thumbSlide.querySelector("[data-media-id]");
// //         if (thumbMedia && thumbMedia.dataset.mediaId === activeMediaId) {
// //             thumbSlide.classList.add("swiper-slide-thumb-active");
// //         }
// //     });
// // }
//
//
//
//             // Helper method to find thumbnail index by media ID
//             getThumbIndexByMediaId(mediaId) {
//                 for (let i = 0; i < this.thumbSwiper.slides.length; i++) {
//                     const thumbMedia = this.thumbSwiper.slides[i].querySelector("[data-media-id]");
//                     if (thumbMedia && thumbMedia.dataset.mediaId === mediaId) {
//                         return i;
//                     }
//                 }
//                 return -1;
//             }
//
//             connectedCallback() {
//                 this.init();
//             }
//
//             init() {
//                 if (!this.settings.sliderElement) return;
//                 this.settings.sliderInstance = new Swiper(
//                     this.settings.sliderElement,
//                     this.settings.options
//                 );
//             }
//
//             setActiveModalOpener(swiper) {
//                 const activeSlide = swiper.slides[swiper.activeIndex];
//                 if (!activeSlide) return;
//                 const activeMediaId = activeSlide.dataset.mediaId;
//                 this.querySelector(
//                     `${this.selectors.modalOpeners}.is-active`
//                 )?.classList.remove("is-active");
//                 this.querySelector(
//                     `${this.selectors.modalOpeners} [data-media-id="${activeMediaId}"]`
//                 )?.parentElement.classList.add("is-active");
//                 if (activeSlide.dataset.mediaType === "model") {
//                     this.querySelector(this.selectors.btnXr).dataset.shopifyModel3dId =
//                         activeMediaId;
//                 }
//             }
//
//
//
//        setActiveMedia(mediaId) {
//     const swiper = this.settings.sliderInstance;
//     if (!swiper) return;
//     const mediaIdStr = String(mediaId);
//     for (let i = 0; i < swiper.slides.length; i++) {
//       const slide = swiper.slides[i];
//       if (slide.dataset.mediaId === mediaIdStr) {
//         const realIndex = parseInt(slide.dataset.swiperSlideIndex);
//         swiper.slideToLoop(isNaN(realIndex) ? i : realIndex);
//         return;
//       }
//     }
//   }
//
//
//
//
//         }
//
//         customElements.define("product-media", ProductMedia);
//
//

//    class ProductMedia extends HTMLElement {
//             constructor() {
//                 super();

//                 this.selectors = {
//                     slider: "[data-slider]",
//                     mediaItem: "[data-media-id]",
//                     modalOpeners: "[data-modal-opener-id]",
//                     zoomImages: "[data-pswp-image]",
//                     btnXr: "[data-shopify-xr]",
//                 };

//                 this.selectedMediaIndex =
//                     Number(
//                         this.querySelector(this.selectors.slider).querySelector(
//                             "[data-selected]"
//                         )?.dataset.index
//                     ) || 0;

//                 const swiperElement = document.getElementById("product__media-slider");
//                 let swiperOptions = {};
//                 try {
//                     const optionsString = this.querySelector(
//                         this.selectors.slider
//                     ).getAttribute("data-swiper-options");
//                     swiperOptions = optionsString ? JSON.parse(optionsString) : {};
//                 } catch (error) {
//                     console.error("Invalid JSON in data-swiper-options:", error);
//                 }

//                 const withThumbs = this.dataset.withThumbs === "true";

//                 // Flag to distinguish between user clicks and programmatic changes
//                 this.isUserInitiatedChange = false;

//                 this.settings = {
//                     sliderElement: this.querySelector(this.selectors.slider),
//                     sliderInstance: null,
//                     options: {
//                         ...swiperOptions,
//                         initialSlide: this.selectedMediaIndex,
//                         navigation: {
//                             prevEl: this.querySelector(".swiper-button-prev"),
//                             nextEl: this.querySelector(".swiper-button-next"),
//                         },
//                         pagination: {
//                             el: ".swiper-pagination",
//                             type: "progressbar",
//                         },
//                         spaceBetween: 2,
//                         watchOverflow: true,
//                         watchSlidesVisibility: true,
//                         watchSlidesProgress: true,
//                         preventInteractionOnTransition: false,
//                         autoHeight: this.dataset.autoHeight === "true",
//                         loop: true,
//                         allowTouchMove: true,
//                         createElements: true,
//                         speed: 600,
//                         effect: "fade",
//                         fadeEffect: {
//                             crossFade: true,
//                         },
//                         breakpoints: {
//                             750: {
//                                 navigation: {
//                                     nextEl: ".swiper-button-next",
//                                     prevEl: ".swiper-button-prev",
//                                 },
//                             },
//                         },
//                         on: {
//                             afterInit: (swiper) => this.afterInit(swiper),
//                             slideChangeTransitionEnd: (swiper) =>
//                                 this.setActiveModalOpener(swiper),
//                             transitionEnd: (swiper) => this.playActiveSlideVideo(swiper),
//                             slideChange: (swiper) => this.syncThumbnails(swiper),
//                         },
//                     },
//                 };

//                 if (withThumbs) {
//                     this.initThumbnails(swiperElement);
//                 } else {
//                     this.settings.options.slidesPerView = 1;
//                 }
//             }

//             afterInit(swiper) {
//                 if (swiper.slides.length > 1) {
//                     this.playActiveSlideVideo(swiper);
//                 }
//             }

//             pauseAllVideos(swiper) {
//                 swiper.el.querySelectorAll("video").forEach((video) => video.pause());
//             }

//             playActiveSlideVideo(swiper) {
//                 const activeSlide = swiper.slides[swiper.activeIndex];
//                 if (!activeSlide) return;
//                 const video = activeSlide.querySelector("video");
//                 if (video) this.playPromise(video);
//             }

//             playPromise(video) {
//                 let playPromise = video.play();
//                 if (playPromise !== undefined) {
//                     playPromise.catch(() => video.pause());
//                 }
//             }

//             initThumbnails(swiperElement) {
//                 const slideCount = this.querySelectorAll("[data-media-id]").length;

//                 this.thumbSwiper = new Swiper(".swiper-thumbs", {
//                     initialSlide: this.selectedMediaIndex,
//                     loopedSlides: slideCount,
//                     direction: "horizontal",
//                     centeredSlides: false,
//                     slidesPerView: 3.5,
//                     spaceBetween: 8,
//                     centeredSlides: false,
//                     freeMode: false,
//                     watchSlidesProgress: true,
//                     watchSlidesVisibility: true,
//                     allowTouchMove: true,
//                     threshold: 5,
//                     breakpoints: {
//                         750: {
//                             loop: true,
//                             spaceBetween: 12,
//                             slidesPerView: "auto",
//                             centeredSlides: false,
//                             slideToClickedSlide: false,
//                             direction: "vertical",
//                         },
//                     },
//                 });

//                 this.settings.options.thumbs = {
//                     swiper: this.thumbSwiper,
//                     autoScrollOffset: 0,
//                     multipleActiveThumbs: false,
//                 };

//                 // Handle thumbnail clicks manually
//                 this.thumbSwiper.on("click", (swiper, event) => {
//                     const clickedSlide = event.target.closest(".swiper-slide");
//                     if (!clickedSlide) return;

//                     const clickedMedia = clickedSlide.querySelector("[data-media-id]");
//                     if (!clickedMedia) return;

//                     const targetMediaId = clickedMedia.dataset.mediaId;

//                     // Set flag to indicate this is a user-initiated change
//                     this.isUserInitiatedChange = true;

//                     // Find matching slide in main slider
//                     const mainSlides = this.settings.sliderInstance.slides;
//                     for (let i = 0; i < mainSlides.length; i++) {
//                         const mainSlide = mainSlides[i];
//                         if (mainSlide.dataset.mediaId === targetMediaId) {
//                             this.settings.sliderInstance.slideTo(i);
//                             break;
//                         }
//                     }
//                 });
//             }

//             updateThumbnailActiveState(mainSwiper) {
//                 const activeSlide = mainSwiper.slides[mainSwiper.activeIndex];
//                 if (!activeSlide) return;

//                 const activeMediaId = activeSlide.dataset.mediaId;

//                 // Remove active class from all thumbnails
//                 this.thumbSwiper.slides.forEach((thumbSlide) => {
//                     thumbSlide.classList.remove("swiper-slide-thumb-active");
//                 });

//                 // Add active class to matching thumbnail
//                 this.thumbSwiper.slides.forEach((thumbSlide) => {
//                     const thumbMedia = thumbSlide.querySelector("[data-media-id]");
//                     if (thumbMedia && thumbMedia.dataset.mediaId === activeMediaId) {
//                         thumbSlide.classList.add("swiper-slide-thumb-active");
//                     }
//                 });
//             }

//             // Check if a slide is currently visible in the thumbnail swiper
//             isThumbSlideVisible(slideIndex) {
//                 if (!this.thumbSwiper) return false;

//                 const slide = this.thumbSwiper.slides[slideIndex];
//                 if (!slide) return false;

//                 const swiperContainer = this.thumbSwiper.el;
//                 const slideRect = slide.getBoundingClientRect();
//                 const containerRect = swiperContainer.getBoundingClientRect();

//                 // Check if slide is at least partially visible
//                 return (
//                     slideRect.left < containerRect.right &&
//                     slideRect.right > containerRect.left &&
//                     slideRect.top < containerRect.bottom &&
//                     slideRect.bottom > containerRect.top
//                 );
//             }

//             // Improved syncThumbnails method
//             syncThumbnails(mainSwiper) {
//                 if (!this.thumbSwiper) return;

//                 const realIndex = mainSwiper.realIndex;

//                 // Always update the active state
//                 this.updateThumbnailActiveState(mainSwiper);

//                 // If this was a user-initiated change (thumbnail click), don't scroll
//                 if (this.isUserInitiatedChange) {
//                     this.isUserInitiatedChange = false;
//                     return;
//                 }

//                 // Check if the active thumbnail is visible
//                 const activeThumbIndex = this.getThumbIndexByMediaId(mainSwiper.slides[mainSwiper.activeIndex].dataset.mediaId);

//                 if (activeThumbIndex !== -1 && !this.isThumbSlideVisible(activeThumbIndex)) {
//                     // Only scroll if the active thumbnail is not visible
//                     this.thumbSwiper.slideToLoop(realIndex, 300);
//                 }
//             }

//             // Helper method to find thumbnail index by media ID
//             getThumbIndexByMediaId(mediaId) {
//                 for (let i = 0; i < this.thumbSwiper.slides.length; i++) {
//                     const thumbMedia = this.thumbSwiper.slides[i].querySelector("[data-media-id]");
//                     if (thumbMedia && thumbMedia.dataset.mediaId === mediaId) {
//                         return i;
//                     }
//                 }
//                 return -1;
//             }

//             connectedCallback() {
//                 this.init();
//             }

//             init() {
//                 if (!this.settings.sliderElement) return;
//                 this.settings.sliderInstance = new Swiper(
//                     this.settings.sliderElement,
//                     this.settings.options
//                 );
//             }

//             setActiveModalOpener(swiper) {
//                 const activeSlide = swiper.slides[swiper.activeIndex];
//                 if (!activeSlide) return;
//                 const activeMediaId = activeSlide.dataset.mediaId;
//                 this.querySelector(
//                     `${this.selectors.modalOpeners}.is-active`
//                 )?.classList.remove("is-active");
//                 this.querySelector(
//                     `${this.selectors.modalOpeners} [data-media-id="${activeMediaId}"]`
//                 )?.parentElement.classList.add("is-active");
//                 if (activeSlide.dataset.mediaType === "model") {
//                     this.querySelector(this.selectors.btnXr).dataset.shopifyModel3dId =
//                         activeMediaId;
//                 }
//             }
//         }

//         customElements.define("product-media", ProductMedia);

// class ProductMedia extends HTMLElement {
//   constructor() {
//     super();

//     this.selectors = {
//       slider: "[data-slider]",
//       mediaItem: "[data-media-id]",
//       modalOpeners: "[data-modal-opener-id]",
//       zoomImages: "[data-pswp-image]",
//       btnXr: "[data-shopify-xr]",
//     };
//     this.selectedMediaIndex =
//       Number(
//         this.querySelector(this.selectors.slider).querySelector(
//           "[data-selected]"
//         )?.dataset.index
//       ) || 0;

//     const swiperElement = document.getElementById("product__media-slider");
//     let swiperOptions = {};
//     try {
//       const optionsString = this.querySelector(
//         this.selectors.slider
//       ).getAttribute("data-swiper-options");
//       swiperOptions = optionsString ? JSON.parse(optionsString) : {};
//     } catch (error) {
//       console.error("Invalid JSON in data-swiper-options:", error);
//     }

//     const withThumbs = this.dataset.withThumbs === "true";

//     this.settings = {
//       sliderElement: this.querySelector(this.selectors.slider),
//       sliderInstance: null,
//       options: {
//         ...swiperOptions,
//         initialSlide: this.selectedMediaIndex,
//         navigation: {
//           prevEl: this.querySelector(".swiper-button-prev"),
//           nextEl: this.querySelector(".swiper-button-next"),
//         },
//         pagination: {
//           el: ".swiper-pagination",
//           type: "progressbar",
//         },
//         spaceBetween: 2,
//         watchOverflow: true,
//         watchSlidesVisibility: true,
//         watchSlidesProgress: true,
//         preventInteractionOnTransition: false,
//         autoHeight: this.dataset.autoHeight === "true",
//         loop: true,
//         allowTouchMove: true,
//         createElements: true,
//         speed: 600,
//         effect: "fade",
//         fadeEffect: {
//           crossFade: true,
//         },
//         breakpoints: {
//           750: {
//             navigation: {
//               nextEl: ".swiper-button-next",
//               prevEl: ".swiper-button-prev",
//             },
//           },
//         },
//         on: {
//           afterInit: (swiper) => this.afterInit(swiper),
//           slideChangeTransitionEnd: (swiper) =>
//             this.setActiveModalOpener(swiper),
//           transitionEnd: (swiper) => this.playActiveSlideVideo(swiper),
//           slideChange: (swiper) => this.syncThumbnails(swiper),
//         },
//       },
//     };

//     if (withThumbs) {
//       this.initThumbnails(swiperElement);
//     } else {
//       this.settings.options.slidesPerView = 1;
//     }
//   }

//   afterInit(swiper) {
//     if (swiper.slides.length > 1) {
//       this.playActiveSlideVideo(swiper);
//     }
//   }

//   pauseAllVideos(swiper) {
//     swiper.el.querySelectorAll("video").forEach((video) => video.pause());
//   }

//   playActiveSlideVideo(swiper) {
//     const activeSlide = swiper.slides[swiper.activeIndex];
//     if (!activeSlide) return;
//     const video = activeSlide.querySelector("video");
//     if (video) this.playPromise(video);
//   }

//   playPromise(video) {
//     let playPromise = video.play();
//     if (playPromise !== undefined) {
//       playPromise.catch(() => video.pause());
//     }
//   }

//   initThumbnails(swiperElement) {
//     const slideCount = this.querySelectorAll("[data-media-id]").length;

//   // initThumbnails(swiperElement) {
//   //   const thumbSwiper = new Swiper(".swiper-thumbs", {
//   //     initialSlide: this.selectedMediaIndex,
//   //     centeredSlides: false,
//   //     centeredSlidesBounds: true,
//   //     freeMode: false,
//   //     direction: "horizontal",
//   //     slidesPerView: 3.5,
//   //     spaceBetween: 8,
//   //     threshold: 5,
//   //     watchOverflow: true,
//   //     watchSlidesVisibility: true,
//   //     watchSlidesProgress: true,
//   //     breakpoints: {
//   //       750: {
//   //         direction: swiperElement.dataset.thumbsDirectionDesktop || "vertical",
//   //         spaceBetween: 8,
//   //         slidesPerView: 3.5,
//   //       },
//   //     },
//   //   });

//     this.thumbSwiper = new Swiper(".swiper-thumbs", {
//       initialSlide: this.selectedMediaIndex,
//       loopedSlides: slideCount,
//       direction: "horizontal",
//       centeredSlides: false,
//       slidesPerView: 3.5,
//       spaceBetween: 8,
//       centeredSlides: false,
//       freeMode: false,
//       watchSlidesProgress: true,
//       watchSlidesVisibility: true,
//       allowTouchMove: true,
//       threshold:5,
//       breakpoints: {
//         750: {
//           loop:true,
//           spaceBetween: 12,
//           slidesPerView: "auto",
//           centeredSlides: false,
//           slideToClickedSlide: false,
//           direction: "vertical",
//         },
//       },
//     });

//     // Link main slider to thumbnails
//     // this.settings.options.loopedSlides = slideCount;
//     this.settings.options.thumbs = {
//       swiper: this.thumbSwiper,
//       slideThumbActiveClass: "swiper-slide-thumb-active",
//       autoScrollOffset: 0,
//       multipleActiveThumbs: false,
//     };

//     // Handle thumbnail clicks manually to avoid loop index conflicts
//     this.thumbSwiper.on("click", (swiper, event) => {
//       const clickedSlide = event.target.closest(".swiper-slide");
//       if (!clickedSlide) return;

//       // Get the media ID from the clicked thumbnail
//       const clickedMedia = clickedSlide.querySelector("[data-media-id]");
//       if (!clickedMedia) return;

//       const targetMediaId = clickedMedia.dataset.mediaId;

//       // Find matching slide in main slider
//       const mainSlides = this.settings.sliderInstance.slides;
//       for (let i = 0; i < mainSlides.length; i++) {
//         const mainSlide = mainSlides[i];
//         if (mainSlide.dataset.mediaId === targetMediaId) {
//           this.settings.sliderInstance.slideTo(i);
//           break;
//         }
//       }
//     });
//   }

//   updateThumbnailActiveState(mainSwiper) {
//     const activeSlide = mainSwiper.slides[mainSwiper.activeIndex];
//     if (!activeSlide) return;

//     const activeMediaId = activeSlide.dataset.mediaId;

//     this.thumbSwiper.slides.forEach((thumbSlide) => {
//       const thumbMedia = thumbSlide.querySelector("[data-media-id]");

//       if (thumbMedia && thumbMedia.dataset.mediaId === activeMediaId) {
//         thumbSlide.classList.add("swiper-slide-thumb-active");
//       }
//     });
//   }
//   syncThumbnails(mainSwiper) {
//     if (!this.thumbSwiper) return;

//     const realIndex = mainSwiper.realIndex;
//     this.updateThumbnailActiveState(mainSwiper);

//     this.thumbSwiper.slideToLoop(realIndex, 300);
//   }

//   connectedCallback() {
//     this.init();
//   }

//   init() {
//     if (!this.settings.sliderElement) return;
//     this.settings.sliderInstance = new Swiper(
//       this.settings.sliderElement,
//       this.settings.options
//     );
//   }

//   setActiveModalOpener(swiper) {
//     const activeSlide = swiper.slides[swiper.activeIndex];
//     if (!activeSlide) return;
//     const activeMediaId = activeSlide.dataset.mediaId;
//     this.querySelector(
//       `${this.selectors.modalOpeners}.is-active`
//     )?.classList.remove("is-active");
//     this.querySelector(
//       `${this.selectors.modalOpeners} [data-media-id="${activeMediaId}"]`
//     )?.parentElement.classList.add("is-active");
//     if (activeSlide.dataset.mediaType === "model") {
//       this.querySelector(this.selectors.btnXr).dataset.shopifyModel3dId =
//         activeMediaId;
//     }
//   }
// }

// customElements.define("product-media", ProductMedia);

// class ProductMedia extends HTMLElement {
//   constructor() {
//     super();

//     this.selectors = {
//       slider: "[data-slider]",
//       mediaItem: "[data-media-id]",
//       modalOpeners: "[data-modal-opener-id]",
//       zoomImages: "[data-pswp-image]",
//       btnXr: "[data-shopify-xr]",
//     };
//     this.selectedMediaIndex =
//       Number(
//         this.querySelector(this.selectors.slider).querySelector(
//           "[data-selected]"
//         )?.dataset.index
//       ) || 0;

//     const swiperElement = document.getElementById("product__media-slider");
//     let swiperOptions = {};
//     try {
//       const optionsString = this.querySelector(
//         this.selectors.slider
//       ).getAttribute("data-swiper-options");
//       swiperOptions = optionsString ? JSON.parse(optionsString) : {};
//     } catch (error) {
//       console.error("Invalid JSON in data-swiper-options:", error);
//     }

//     const withThumbs = this.dataset.withThumbs === "true";

//     this.settings = {
//       sliderElement: this.querySelector(this.selectors.slider),
//       sliderInstance: null,
//       options: {
//         ...swiperOptions,
//         initialSlide: this.selectedMediaIndex,
//         navigation: {
//           prevEl: this.querySelector(".swiper-button-prev"),
//           nextEl: this.querySelector(".swiper-button-next"),
//         },
//         pagination: {
//           el: ".swiper-pagination",
//           type: "progressbar",
//         },
//         spaceBetween: 2,
//         watchOverflow: true,
//         watchSlidesVisibility: true,
//         watchSlidesProgress: true,
//         preventInteractionOnTransition: false,
//         autoHeight: this.dataset.autoHeight === "true",
//         loop: true,
//         allowTouchMove: true,
//         speed: 500,
//         on: {
//           afterInit: (swiper) => this.afterInit(swiper),
//           slideChangeTransitionEnd: (swiper) =>
//             this.setActiveModalOpener(swiper),
//           transitionStart: (swiper) => this.pauseAllVideos(swiper),
//           transitionEnd: (swiper) => this.playActiveSlideVideo(swiper),
//         },
//       },
//     };

//     if (withThumbs) {
//       this.initThumbnails(swiperElement);
//     } else {
//       this.settings.options.slidesPerView = 1;
//     }
//   }

//   afterInit(swiper) {
//     if (swiper.slides.length > 1) {
//       this.playActiveSlideVideo(swiper);
//     }
//   }

//   pauseAllVideos(swiper) {
//     swiper.el.querySelectorAll("video").forEach((video) => video.pause());
//   }

//   playActiveSlideVideo(swiper) {
//     const activeSlide = swiper.slides[swiper.activeIndex];
//     if (!activeSlide) return;
//     const video = activeSlide.querySelector("video");
//     if (video) this.playPromise(video);
//   }

//   playPromise(video) {
//     let playPromise = video.play();
//     if (playPromise !== undefined) {
//       playPromise.catch(() => video.pause());
//     }
//   }

//   initThumbnails(swiperElement) {
//     const thumbSwiper = new Swiper(".swiper-thumbs", {
//       initialSlide: this.selectedMediaIndex,
//       centeredSlides: false,
//       centeredSlidesBounds: true,
//       freeMode: false,
//       direction: "horizontal",
//       slidesPerView: 3.5,
//       spaceBetween: 8,
//       threshold: 5,
//       watchOverflow: true,
//       watchSlidesVisibility: true,
//       watchSlidesProgress: true,
//       breakpoints: {
//         750: {
//           direction: swiperElement.dataset.thumbsDirectionDesktop || "vertical",
//           spaceBetween: 8,
//           slidesPerView: 3.5,
//         },
//       },
//     });

//     this.settings.options.thumbs = { swiper: thumbSwiper };

//     this.settings.options.on.slideChange = (swiper) => {
//       const currentIndex = swiper.realIndex;
//       if (currentIndex >= 3) {
//         thumbSwiper.slideTo(currentIndex - 2);
//       } else {
//         thumbSwiper.slideTo(0);
//       }
//     };
//   }

//   connectedCallback() {
//     this.init();
//     this.attachArrowEvents();
//   }

//   init() {
//     if (!this.settings.sliderElement) return;
//     this.settings.sliderInstance = new Swiper(
//       this.settings.sliderElement,
//       this.settings.options
//     );
//   }

//   attachArrowEvents() {
//     const prevBtn = this.querySelector(".swiper-button-prev");
//     const nextBtn = this.querySelector(".swiper-button-next");
//     if (prevBtn && nextBtn && this.settings.sliderInstance) {
//       prevBtn.addEventListener("click", () => {
//         this.settings.sliderInstance.slidePrev();
//       });
//       nextBtn.addEventListener("click", () => {
//         this.settings.sliderInstance.slideNext();
//       });
//     }
//   }

//   setActiveModalOpener(swiper) {
//     const activeSlide = swiper.slides[swiper.activeIndex];
//     if (!activeSlide) return;
//     const activeMediaId = activeSlide.dataset.mediaId;
//     this.querySelector(
//       `${this.selectors.modalOpeners}.is-active`
//     )?.classList.remove("is-active");
//     this.querySelector(
//       `${this.selectors.modalOpeners} [data-media-id="${activeMediaId}"]`
//     )?.parentElement.classList.add("is-active");
//     if (activeSlide.dataset.mediaType === "model") {
//       this.querySelector(this.selectors.btnXr).dataset.shopifyModel3dId =
//         activeMediaId;
//     }
//   }
// }

// customElements.define("product-media", ProductMedia);

// // class ProductMedia extends HTMLElement {
// //   constructor() {
// //     super();

// //     this.selectors = {
// //       slider: "[data-slider]",
// //       mediaItem: "[data-media-id]",
// //       modalOpeners: "[data-modal-opener-id]",
// //       zoomImages: "[data-pswp-image]",
// //       btnXr: "[data-shopify-xr]",
// //     };
// //     this.selectedMediaIndex =
// //       Number(
// //         this.querySelector(this.selectors.slider).querySelector(
// //           "[data-selected]"
// //         )?.dataset.index
// //       ) || 0;

// //     const swiperElement = document.getElementById("product__media-slider");
// //     let swiperOptions = {};
// //     try {
// //       const optionsString = this.querySelector(
// //         this.selectors.slider
// //       ).getAttribute("data-swiper-options");
// //       swiperOptions = optionsString ? JSON.parse(optionsString) : {};
// //     } catch (error) {
// //       console.error("Invalid JSON in data-swiper-options:", error);
// //     }

// //     const withThumbs = this.dataset.withThumbs === "true";

// //     this.settings = {
// //       sliderElement: this.querySelector(this.selectors.slider),
// //       sliderInstance: null,
// //       options: {
// //         ...swiperOptions,
// //         initialSlide: this.selectedMediaIndex,
// //         navigation: {
// //           prevEl: this.querySelector(".swiper-button-prev"),
// //           nextEl: this.querySelector(".swiper-button-next"),
// //         },
// //         pagination: {
// //           el: ".swiper-pagination",
// //           type: "progressbar",
// //         },
// //         spaceBetween: 2,
// //         watchOverflow: true,
// //         watchSlidesVisibility: true,
// //         watchSlidesProgress: true,
// //         preventInteractionOnTransition: false,
// //         autoHeight: this.dataset.autoHeight === "true",
// //         loop: true,
// //         allowTouchMove: true,
// //         speed: 0, // Instant swap - no animation
// //         on: {
// //           afterInit: (swiper) => this.afterInit(swiper),
// //           slideChangeTransitionEnd: (swiper) =>
// //             this.setActiveModalOpener(swiper),
// //           transitionStart: (swiper) => this.pauseAllVideos(swiper),
// //           transitionEnd: (swiper) => this.playActiveSlideVideo(swiper),
// //         },
// //       },
// //     };

// //     if (withThumbs) {
// //       this.initThumbnails(swiperElement);
// //     } else {
// //       this.settings.options.slidesPerView = 1;
// //     }
// //   }

// //   afterInit(swiper) {
// //     if (swiper.slides.length > 1) {
// //       this.playActiveSlideVideo(swiper);
// //     }
// //   }

// //   pauseAllVideos(swiper) {
// //     swiper.el.querySelectorAll("video").forEach((video) => video.pause());
// //   }

// //   playActiveSlideVideo(swiper) {
// //     const activeSlide = swiper.slides[swiper.activeIndex];
// //     if (!activeSlide) return;
// //     const video = activeSlide.querySelector("video");
// //     if (video) this.playPromise(video);
// //   }

// //   playPromise(video) {
// //     let playPromise = video.play();
// //     if (playPromise !== undefined) {
// //       playPromise.catch(() => video.pause());
// //     }
// //   }

// //   initThumbnails(swiperElement) {
// //     const thumbSwiper = new Swiper(".swiper-thumbs", {
// //       initialSlide: this.selectedMediaIndex,
// //       centeredSlides: false,
// //       centeredSlidesBounds: true,
// //       freeMode: false,
// //       direction: "horizontal",
// //       slidesPerView: 3.5,
// //       spaceBetween: 8,
// //       threshold: 5,
// //       watchOverflow: true,
// //       watchSlidesVisibility: true,
// //       watchSlidesProgress: true,
// //       loop: true, // Add loop for thumbnails
// //       breakpoints: {
// //         750: {
// //           direction: swiperElement.dataset.thumbsDirectionDesktop || "vertical",
// //           spaceBetween: 8,
// //           slidesPerView: 3.5,
// //           allowTouchMove: false, // Disable horizontal scrolling on desktop
// //           loop: true, // Keep loop enabled for vertical thumbnails
// //         },
// //       },
// //     });

// //     this.settings.options.thumbs = { swiper: thumbSwiper };

// //     this.settings.options.on.slideChange = (swiper) => {
// //       const currentIndex = swiper.realIndex;
// //       thumbSwiper.slideTo(currentIndex);
// //     };
// //   }

// //   connectedCallback() {
// //     this.init();
// //     this.attachArrowEvents();
// //   }

// //   init() {
// //     if (!this.settings.sliderElement) return;
// //     this.settings.sliderInstance = new Swiper(
// //       this.settings.sliderElement,
// //       this.settings.options
// //     );
// //     this.swiperInstance = this.settings.sliderInstance;
// //   }

// //   attachArrowEvents() {
// //     const prevBtn = this.querySelector(".swiper-button-prev");
// //     const nextBtn = this.querySelector(".swiper-button-next");
// //     if (prevBtn && nextBtn && this.settings.sliderInstance) {
// //       prevBtn.addEventListener("click", () => {
// //         this.settings.sliderInstance.slidePrev();
// //       });
// //       nextBtn.addEventListener("click", () => {
// //         this.settings.sliderInstance.slideNext();
// //       });
// //     }
// //   }

// //   setActiveModalOpener(swiper) {
// //     const activeSlide = swiper.slides[swiper.activeIndex];
// //     if (!activeSlide) return;
// //     const activeMediaId = activeSlide.dataset.mediaId;
// //     this.querySelector(
// //       `${this.selectors.modalOpeners}.is-active`
// //     )?.classList.remove("is-active");
// //     this.querySelector(
// //       `${this.selectors.modalOpeners} [data-media-id="${activeMediaId}"]`
// //     )?.parentElement.classList.add("is-active");
// //     if (activeSlide.dataset.mediaType === "model") {
// //       this.querySelector(this.selectors.btnXr).dataset.shopifyModel3dId =
// //         activeMediaId;
// //     }
// //   }
// // }

// // customElements.define("product-media", ProductMedia);

// // class ProductMedia extends HTMLElement {
// //   constructor() {
// //     super();

// //     this.selectors = {
// //       slider: "[data-slider]",
// //       mediaItem: "[data-media-id]",
// //       modalOpeners: "[data-modal-opener-id]",
// //       zoomImages: "[data-pswp-image]",
// //       btnXr: "[data-shopify-xr]",
// //     };
// //     this.selectedMediaIndex =
// //       Number(
// //         this.querySelector(this.selectors.slider).querySelector(
// //           "[data-selected]"
// //         )?.dataset.index
// //       ) || 0;

// //     const swiperElement = document.getElementById("product__media-slider");
// //     let swiperOptions = {};
// //     try {
// //       const optionsString = this.querySelector(
// //         this.selectors.slider
// //       ).getAttribute("data-swiper-options");
// //       swiperOptions = optionsString ? JSON.parse(optionsString) : {};
// //     } catch (error) {
// //       console.error("Invalid JSON in data-swiper-options:", error);
// //     }

// //     const withThumbs = this.dataset.withThumbs === "true";
// //     this.isDesktop = window.innerWidth >= 750;

// //     this.settings = {
// //       sliderElement: this.querySelector(this.selectors.slider),
// //       sliderInstance: null,
// //       options: {
// //         ...swiperOptions,
// //         initialSlide: this.selectedMediaIndex,
// //         navigation: {
// //           prevEl: this.querySelector(".swiper-button-prev"),
// //           nextEl: this.querySelector(".swiper-button-next"),
// //         },
// //         pagination: {
// //           el: ".swiper-pagination",
// //           type: "progressbar",
// //         },
// //         spaceBetween: 2,
// //         watchOverflow: true,
// //         watchSlidesVisibility: true,
// //         watchSlidesProgress: true,
// //         preventInteractionOnTransition: false,
// //         autoHeight: this.dataset.autoHeight === "true",
// //         loop: true,
// //         allowTouchMove: !this.isDesktop, // Disable swipe on desktop
// //         speed: 0, // Instant image swap
// //         effect: "fade",
// //         fadeEffect: {
// //           crossFade: true,
// //         },
// //         on: {
// //           afterInit: (swiper) => this.afterInit(swiper),
// //           slideChangeTransitionEnd: (swiper) =>
// //             this.setActiveModalOpener(swiper),
// //           transitionStart: (swiper) => this.pauseAllVideos(swiper),
// //           transitionEnd: (swiper) => this.playActiveSlideVideo(swiper),
// //         },
// //       },
// //     };

// //     // Handle thumbnails based on device
// //     if (withThumbs) {
// //       if (this.isDesktop) {
// //         // Desktop: Use custom vertical thumbnails
// //         this.settings.options.slidesPerView = 1;
// //       } else {
// //         // Mobile: Use Swiper thumbnails
// //         this.initThumbnails(swiperElement);
// //       }
// //     } else {
// //       this.settings.options.slidesPerView = 1;
// //     }
// //   }

// //   afterInit(swiper) {
// //     if (swiper.slides.length > 1) {
// //       this.playActiveSlideVideo(swiper);
// //     }

// //     // Initialize custom thumbnails on desktop
// //     if (this.isDesktop && this.dataset.withThumbs === "true") {
// //       this.initCustomThumbnails();
// //     }
// //   }

// //   initCustomThumbnails() {
// //     this.thumbnailContainer = this.querySelector(".swiper-thumbs");
// //     this.thumbnails = this.querySelectorAll(".swiper-thumbs .swiper-slide");

// //     if (!this.thumbnailContainer || !this.thumbnails.length) return;

// //     // Make thumbnails vertical
// //     this.makeThumbsVertical();
// //     this.bindThumbnailEvents();
// //     this.updateActiveThumbnail(this.selectedMediaIndex);
// //   }

// //   makeThumbsVertical() {
// //     const thumbsContainer = this.querySelector(".swiper-thumbs-container");
// //     const thumbsSwiper = this.querySelector(".swiper-thumbs");

// //     if (thumbsContainer) {
// //       thumbsContainer.style.height = "auto";
// //       thumbsContainer.style.maxHeight = "500px";
// //       thumbsContainer.style.overflowY = "auto";
// //     }

// //     if (thumbsSwiper) {
// //       // thumbsSwiper.style.display = "flex";
// //       // thumbsSwiper.style.flexDirection = "column";
// //       thumbsSwiper.style.height = "auto";
// //     }

// //     // Style individual thumbnails
// //     this.thumbnails.forEach((thumb) => {
// //       thumb.style.width = "100%";
// //       thumb.style.marginBottom = "8px";
// //     });
// //   }

// //   bindThumbnailEvents() {
// //     this.thumbnails.forEach((thumbnail, index) => {
// //       thumbnail.addEventListener("click", () => {
// //         this.goToSlide(index);
// //       });
// //     });

// //     // Handle scroll loop
// //     this.thumbnailContainer.addEventListener("scroll", () => {
// //       this.handleThumbScrollLoop();
// //     });

// //     // Main slider change event
// //     if (this.settings.sliderInstance) {
// //       this.settings.sliderInstance.on("slideChange", (swiper) => {
// //         this.updateActiveThumbnail(swiper.activeIndex);
// //       });
// //     }
// //   }

// //   goToSlide(index) {
// //     if (this.settings.sliderInstance) {
// //       this.settings.sliderInstance.slideTo(index);
// //     }
// //     this.updateActiveThumbnail(index);
// //   }

// //   updateActiveThumbnail(activeIndex) {
// //     if (!this.thumbnails) return;

// //     this.thumbnails.forEach((thumbnail, index) => {
// //       thumbnail.classList.toggle(
// //         "swiper-slide-thumb-active",
// //         index === activeIndex
// //       );
// //     });

// //     const activeThumbnail = this.thumbnails[activeIndex];
// //     if (activeThumbnail) {
// //       this.scrollThumbnailIntoView(activeThumbnail);
// //     }
// //   }

// //   handleThumbScrollLoop() {
// //     const container = this.thumbnailContainer;
// //     const scrollTop = container.scrollTop;
// //     const scrollHeight = container.scrollHeight;
// //     const clientHeight = container.clientHeight;

// //     // Loop to bottom when scrolled to top
// //     if (scrollTop <= 0) {
// //       container.scrollTop = scrollHeight - clientHeight - 1;
// //     }

// //     // Loop to top when scrolled to bottom
// //     if (scrollTop + clientHeight >= scrollHeight) {
// //       container.scrollTop = 1;
// //     }
// //   }

// //   scrollThumbnailIntoView(thumbnail) {
// //     const container = this.thumbnailContainer;
// //     const containerHeight = container.clientHeight;
// //     const thumbnailTop = thumbnail.offsetTop;
// //     const thumbnailHeight = thumbnail.offsetHeight;
// //     const scrollTop = container.scrollTop;

// //     if (thumbnailTop < scrollTop) {
// //       container.scrollTo({
// //         top: thumbnailTop - 10,
// //         behavior: "smooth",
// //       });
// //     } else if (thumbnailTop + thumbnailHeight > scrollTop + containerHeight) {
// //       container.scrollTo({
// //         top: thumbnailTop + thumbnailHeight - containerHeight + 10,
// //         behavior: "smooth",
// //       });
// //     }
// //   }

// //   pauseAllVideos(swiper) {
// //     swiper.el.querySelectorAll("video").forEach((video) => video.pause());
// //   }

// //   playActiveSlideVideo(swiper) {
// //     const activeSlide = swiper.slides[swiper.activeIndex];
// //     if (!activeSlide) return;
// //     const video = activeSlide.querySelector("video");
// //     if (video) this.playPromise(video);
// //   }

// //   playPromise(video) {
// //     let playPromise = video.play();
// //     if (playPromise !== undefined) {
// //       playPromise.catch(() => video.pause());
// //     }
// //   }

// //   initThumbnails(swiperElement) {
// //     // Mobile only - horizontal thumbnails
// //     const thumbSwiper = new Swiper(".swiper-thumbs", {
// //       initialSlide: this.selectedMediaIndex,
// //       centeredSlides: false,
// //       centeredSlidesBounds: true,
// //       freeMode: false,
// //       direction: "horizontal",
// //       slidesPerView: 3.5,
// //       spaceBetween: 8,
// //       threshold: 5,
// //       watchOverflow: true,
// //       watchSlidesVisibility: true,
// //       watchSlidesProgress: true,
// //       loop: true,
// //     });

// //     this.settings.options.thumbs = { swiper: thumbSwiper };

// //     this.settings.options.on.slideChange = (swiper) => {
// //       const currentIndex = swiper.realIndex;
// //       thumbSwiper.slideTo(currentIndex);
// //     };
// //   }

// //   connectedCallback() {
// //     this.init();
// //     this.attachArrowEvents();
// //   }

// //   init() {
// //     if (!this.settings.sliderElement) return;
// //     this.settings.sliderInstance = new Swiper(
// //       this.settings.sliderElement,
// //       this.settings.options
// //     );
// //     this.swiperInstance = this.settings.sliderInstance;
// //   }

// //   attachArrowEvents() {
// //     const prevBtn = this.querySelector(".swiper-button-prev");
// //     const nextBtn = this.querySelector(".swiper-button-next");
// //     if (prevBtn && nextBtn && this.settings.sliderInstance) {
// //       prevBtn.addEventListener("click", () => {
// //         this.settings.sliderInstance.slidePrev();
// //       });
// //       nextBtn.addEventListener("click", () => {
// //         this.settings.sliderInstance.slideNext();
// //       });
// //     }
// //   }

// //   setActiveModalOpener(swiper) {
// //     const activeSlide = swiper.slides[swiper.activeIndex];
// //     if (!activeSlide) return;
// //     const activeMediaId = activeSlide.dataset.mediaId;
// //     this.querySelector(
// //       `${this.selectors.modalOpeners}.is-active`
// //     )?.classList.remove("is-active");
// //     this.querySelector(
// //       `${this.selectors.modalOpeners} [data-media-id="${activeMediaId}"]`
// //     )?.parentElement.classList.add("is-active");
// //     if (activeSlide.dataset.mediaType === "model") {
// //       this.querySelector(this.selectors.btnXr).dataset.shopifyModel3dId =
// //         activeMediaId;
// //     }
// //   }
// // }

// // customElements.define("product-media", ProductMedia);

// // class ProductMedia extends HTMLElement {
// //   constructor() {
// //     super();

// //     this.selectors = {
// //       slider: "[data-slider]",
// //       mediaItem: "[data-media-id]",
// //       modalOpeners: "[data-modal-opener-id]",
// //       zoomImages: "[data-pswp-image]",
// //       btnXr: "[data-shopify-xr]",
// //     };
// //     this.selectedMediaIndex =
// //       Number(
// //         this.querySelector(this.selectors.slider).querySelector(
// //           "[data-selected]"
// //         )?.dataset.index
// //       ) || 0;

// //     const swiperElement = document.getElementById("product__media-slider");
// //     let swiperOptions = {};
// //     try {
// //       const optionsString = this.querySelector(
// //         this.selectors.slider
// //       ).getAttribute("data-swiper-options");
// //       swiperOptions = optionsString ? JSON.parse(optionsString) : {};
// //     } catch (error) {
// //       console.error("Invalid JSON in data-swiper-options:", error);
// //     }

// //     const withThumbs = this.dataset.withThumbs === "true";
// //     const isDesktop = window.innerWidth >= 750;

// //     this.settings = {
// //       sliderElement: this.querySelector(this.selectors.slider),
// //       sliderInstance: null,
// //       options: {
// //         ...swiperOptions,
// //         initialSlide: this.selectedMediaIndex,
// //         navigation: {
// //           prevEl: this.querySelector(".swiper-button-prev"),
// //           nextEl: this.querySelector(".swiper-button-next"),
// //         },
// //         pagination: {
// //           el: ".swiper-pagination",
// //           type: "progressbar",
// //         },
// //         spaceBetween: 2,
// //         watchOverflow: true,
// //         watchSlidesVisibility: true,
// //         watchSlidesProgress: true,
// //         preventInteractionOnTransition: false,
// //         autoHeight: this.dataset.autoHeight === "true",
// //         loop: true,
// //         allowTouchMove: !isDesktop, // Disable swipe on desktop
// //         speed: 0, // Instant image swap
// //         effect: "fade",
// //         fadeEffect: {
// //           crossFade: true,
// //         },
// //         on: {
// //           afterInit: (swiper) => this.afterInit(swiper),
// //           slideChangeTransitionEnd: (swiper) =>
// //             this.setActiveModalOpener(swiper),
// //           transitionStart: (swiper) => this.pauseAllVideos(swiper),
// //           transitionEnd: (swiper) => this.playActiveSlideVideo(swiper),
// //         },
// //       },
// //     };

// //     // Only init Swiper thumbnails on mobile - desktop uses CustomThumbnails
// //     if (withThumbs && !isDesktop) {
// //       this.initThumbnails(swiperElement);
// //     } else {
// //       this.settings.options.slidesPerView = 1;
// //       // Don't initialize Swiper thumbnails on desktop
// //       this.settings.options.thumbs = null;
// //     }
// //   }

// //   afterInit(swiper) {
// //     if (swiper.slides.length > 1) {
// //       this.playActiveSlideVideo(swiper);
// //     }
// //   }

// //   pauseAllVideos(swiper) {
// //     swiper.el.querySelectorAll("video").forEach((video) => video.pause());
// //   }

// //   playActiveSlideVideo(swiper) {
// //     const activeSlide = swiper.slides[swiper.activeIndex];
// //     if (!activeSlide) return;
// //     const video = activeSlide.querySelector("video");
// //     if (video) this.playPromise(video);
// //   }

// //   playPromise(video) {
// //     let playPromise = video.play();
// //     if (playPromise !== undefined) {
// //       playPromise.catch(() => video.pause());
// //     }
// //   }

// //   initThumbnails(swiperElement) {
// //     const thumbSwiper = new Swiper(".swiper-thumbs", {
// //       initialSlide: this.selectedMediaIndex,
// //       centeredSlides: false,
// //       centeredSlidesBounds: true,
// //       freeMode: false,
// //       direction: "horizontal",
// //       slidesPerView: 3.5,
// //       spaceBetween: 8,
// //       threshold: 5,
// //       watchOverflow: true,
// //       watchSlidesVisibility: true,
// //       watchSlidesProgress: true,
// //       loop: true,
// //     });

// //     this.settings.options.thumbs = { swiper: thumbSwiper };

// //     this.settings.options.on.slideChange = (swiper) => {
// //       const currentIndex = swiper.realIndex;
// //       thumbSwiper.slideTo(currentIndex);
// //     };
// //   }

// //   connectedCallback() {
// //     this.init();
// //     this.attachArrowEvents();
// //   }

// //   init() {
// //     if (!this.settings.sliderElement) return;
// //     this.settings.sliderInstance = new Swiper(
// //       this.settings.sliderElement,
// //       this.settings.options
// //     );
// //     this.swiperInstance = this.settings.sliderInstance;
// //   }

// //   attachArrowEvents() {
// //     const prevBtn = this.querySelector(".swiper-button-prev");
// //     const nextBtn = this.querySelector(".swiper-button-next");
// //     if (prevBtn && nextBtn && this.settings.sliderInstance) {
// //       prevBtn.addEventListener("click", () => {
// //         this.settings.sliderInstance.slidePrev();
// //       });
// //       nextBtn.addEventListener("click", () => {
// //         this.settings.sliderInstance.slideNext();
// //       });
// //     }
// //   }

// //   setActiveModalOpener(swiper) {
// //     const activeSlide = swiper.slides[swiper.activeIndex];
// //     if (!activeSlide) return;
// //     const activeMediaId = activeSlide.dataset.mediaId;
// //     this.querySelector(
// //       `${this.selectors.modalOpeners}.is-active`
// //     )?.classList.remove("is-active");
// //     this.querySelector(
// //       `${this.selectors.modalOpeners} [data-media-id="${activeMediaId}"]`
// //     )?.parentElement.classList.add("is-active");
// //     if (activeSlide.dataset.mediaType === "model") {
// //       this.querySelector(this.selectors.btnXr).dataset.shopifyModel3dId =
// //         activeMediaId;
// //     }
// //   }
// // }

// // customElements.define("product-media", ProductMedia);

// // class ProductMedia extends HTMLElement {
// //   constructor() {
// //     super();

// //     this.selectors = {
// //       slider: "[data-slider]",
// //       mediaItem: "[data-media-id]",
// //       modalOpeners: "[data-modal-opener-id]",
// //       zoomImages: "[data-pswp-image]",
// //       btnXr: "[data-shopify-xr]",
// //     };
// //     this.selectedMediaIndex =
// //       Number(
// //         this.querySelector(this.selectors.slider).querySelector(
// //           "[data-selected]"
// //         )?.dataset.index
// //       ) || 0;

// //     const swiperElement = document.getElementById("product__media-slider");
// //     let swiperOptions = {};
// //     try {
// //       const optionsString = this.querySelector(
// //         this.selectors.slider
// //       ).getAttribute("data-swiper-options");
// //       swiperOptions = optionsString ? JSON.parse(optionsString) : {};
// //     } catch (error) {
// //       console.error("Invalid JSON in data-swiper-options:", error);
// //     }

// //     const withThumbs = this.dataset.withThumbs === "true";
// //     const isDesktop = window.innerWidth >= 750;

// //     this.settings = {
// //       sliderElement: this.querySelector(this.selectors.slider),
// //       sliderInstance: null,
// //       options: {
// //         ...swiperOptions,
// //         initialSlide: this.selectedMediaIndex,
// //         navigation: {
// //           prevEl: this.querySelector(".swiper-button-prev"),
// //           nextEl: this.querySelector(".swiper-button-next"),
// //         },
// //         pagination: {
// //           el: ".swiper-pagination",
// //           type: "progressbar",
// //         },
// //         spaceBetween: 2,
// //         watchOverflow: true,
// //         watchSlidesVisibility: true,
// //         watchSlidesProgress: true,
// //         preventInteractionOnTransition: false,
// //         autoHeight: this.dataset.autoHeight === "true",
// //         loop: true,
// //         allowTouchMove: !isDesktop, // Disable swipe on desktop
// //         speed: 0, // Instant image swap
// //         effect: "fade",
// //         fadeEffect: {
// //           crossFade: true,
// //         },
// //         on: {
// //           afterInit: (swiper) => this.afterInit(swiper),
// //           slideChangeTransitionEnd: (swiper) =>
// //             this.setActiveModalOpener(swiper),
// //           transitionStart: (swiper) => this.pauseAllVideos(swiper),
// //           transitionEnd: (swiper) => this.playActiveSlideVideo(swiper),
// //         },
// //       },
// //     };

// //     // Only init thumbnails on mobile
// //     if (withThumbs && !isDesktop) {
// //       this.initThumbnails(swiperElement);
// //     } else {
// //       this.settings.options.slidesPerView = 1;
// //     }
// //   }

// //   afterInit(swiper) {
// //     if (swiper.slides.length > 1) {
// //       this.playActiveSlideVideo(swiper);
// //     }
// //   }

// //   pauseAllVideos(swiper) {
// //     swiper.el.querySelectorAll("video").forEach((video) => video.pause());
// //   }

// //   playActiveSlideVideo(swiper) {
// //     const activeSlide = swiper.slides[swiper.activeIndex];
// //     if (!activeSlide) return;
// //     const video = activeSlide.querySelector("video");
// //     if (video) this.playPromise(video);
// //   }

// //   playPromise(video) {
// //     let playPromise = video.play();
// //     if (playPromise !== undefined) {
// //       playPromise.catch(() => video.pause());
// //     }
// //   }

// //   initThumbnails(swiperElement) {
// //     const thumbSwiper = new Swiper(".swiper-thumbs", {
// //       initialSlide: this.selectedMediaIndex,
// //       centeredSlides: false,
// //       centeredSlidesBounds: true,
// //       freeMode: false,
// //       direction: "horizontal",
// //       slidesPerView: 3.5,
// //       spaceBetween: 8,
// //       threshold: 5,
// //       watchOverflow: true,
// //       watchSlidesVisibility: true,
// //       watchSlidesProgress: true,
// //       loop: true,
// //     });

// //     this.settings.options.thumbs = { swiper: thumbSwiper };

// //     this.settings.options.on.slideChange = (swiper) => {
// //       const currentIndex = swiper.realIndex;
// //       thumbSwiper.slideTo(currentIndex);
// //     };
// //   }

// //   connectedCallback() {
// //     this.init();
// //     this.attachArrowEvents();
// //   }

// //   init() {
// //     if (!this.settings.sliderElement) return;
// //     this.settings.sliderInstance = new Swiper(
// //       this.settings.sliderElement,
// //       this.settings.options
// //     );
// //     this.swiperInstance = this.settings.sliderInstance;
// //   }

// //   attachArrowEvents() {
// //     const prevBtn = this.querySelector(".swiper-button-prev");
// //     const nextBtn = this.querySelector(".swiper-button-next");
// //     if (prevBtn && nextBtn && this.settings.sliderInstance) {
// //       prevBtn.addEventListener("click", () => {
// //         this.settings.sliderInstance.slidePrev();
// //       });
// //       nextBtn.addEventListener("click", () => {
// //         this.settings.sliderInstance.slideNext();
// //       });
// //     }
// //   }

// //   setActiveModalOpener(swiper) {
// //     const activeSlide = swiper.slides[swiper.activeIndex];
// //     if (!activeSlide) return;
// //     const activeMediaId = activeSlide.dataset.mediaId;
// //     this.querySelector(
// //       `${this.selectors.modalOpeners}.is-active`
// //     )?.classList.remove("is-active");
// //     this.querySelector(
// //       `${this.selectors.modalOpeners} [data-media-id="${activeMediaId}"]`
// //     )?.parentElement.classList.add("is-active");
// //     if (activeSlide.dataset.mediaType === "model") {
// //       this.querySelector(this.selectors.btnXr).dataset.shopifyModel3dId =
// //         activeMediaId;
// //     }
// //   }
// // }

// // customElements.define("product-media", ProductMedia);

// // // class ProductMedia extends HTMLElement {
// // //   constructor() {
// // //     super();

// // //     this.selectors = {
// // //       slider: "[data-slider]",
// // //       mediaItem: "[data-media-id]",
// // //       modalOpeners: "[data-modal-opener-id]",
// // //       zoomImages: "[data-pswp-image]",
// // //       btnXr: "[data-shopify-xr]",
// // //     };
// // //     this.selectedMediaIndex =
// // //       Number(
// // //         this.querySelector(this.selectors.slider).querySelector(
// // //           "[data-selected]"
// // //         )?.dataset.index
// // //       ) || 0;

// // //     const swiperElement = document.getElementById("product__media-slider");
// // //     let swiperOptions = {};
// // //     try {
// // //       const optionsString = this.querySelector(
// // //         this.selectors.slider
// // //       ).getAttribute("data-swiper-options");
// // //       swiperOptions = optionsString ? JSON.parse(optionsString) : {};
// // //     } catch (error) {
// // //       console.error("Invalid JSON in data-swiper-options:", error);
// // //     }

// // //     const withThumbs = this.dataset.withThumbs === "true";
// // //     //const hasCustomThumbnails = document.getElementById("custom-thumbnails");

// // //     this.settings = {
// // //       sliderElement: this.querySelector(this.selectors.slider),
// // //       sliderInstance: null,
// // //       options: {
// // //         ...swiperOptions,
// // //         initialSlide: this.selectedMediaIndex,
// // //         navigation: {
// // //           prevEl: this.querySelector(".swiper-button-prev"),
// // //           nextEl: this.querySelector(".swiper-button-next"),
// // //         },
// // //         pagination: {
// // //           el: ".swiper-pagination",
// // //           type: "progressbar",
// // //         },
// // //         spaceBetween: 2,
// // //         watchOverflow: true,
// // //         watchSlidesVisibility: true,
// // //         watchSlidesProgress: true,
// // //         preventInteractionOnTransition: false,
// // //         autoHeight: this.dataset.autoHeight === "true",
// // //         loop: true,
// // //         allowTouchMove: true,
// // //         speed: 500,
// // //         on: {
// // //           afterInit: (swiper) => this.afterInit(swiper),
// // //           slideChangeTransitionEnd: (swiper) =>
// // //             this.setActiveModalOpener(swiper),
// // //           transitionStart: (swiper) => this.pauseAllVideos(swiper),
// // //           transitionEnd: (swiper) => this.playActiveSlideVideo(swiper),
// // //         },
// // //       },
// // //     };

// // //     // if (withThumbs && !hasCustomThumbnails) {
// // //     //   this.initThumbnails(swiperElement);
// // //     // } else {
// // //     //   this.settings.options.slidesPerView = 1;
// // //     // }
// // //     if (withThumbs) {
// // //       this.initThumbnails(swiperElement);
// // //     } else {
// // //       this.settings.options.slidesPerView = 1;
// // //     }
// // //   }

// // //   afterInit(swiper) {
// // //     if (swiper.slides.length > 1) {
// // //       this.playActiveSlideVideo(swiper);
// // //     }
// // //   }

// // //   pauseAllVideos(swiper) {
// // //     swiper.el.querySelectorAll("video").forEach((video) => video.pause());
// // //   }

// // //   playActiveSlideVideo(swiper) {
// // //     const activeSlide = swiper.slides[swiper.activeIndex];
// // //     if (!activeSlide) return;
// // //     const video = activeSlide.querySelector("video");
// // //     if (video) this.playPromise(video);
// // //   }

// // //   playPromise(video) {
// // //     let playPromise = video.play();
// // //     if (playPromise !== undefined) {
// // //       playPromise.catch(() => video.pause());
// // //     }
// // //   }

// // //   initThumbnails(swiperElement) {
// // //     const thumbSwiper = new Swiper(".swiper-thumbs", {
// // //       initialSlide: this.selectedMediaIndex,
// // //       centeredSlides: false,
// // //       centeredSlidesBounds: true,
// // //       freeMode: false,
// // //       direction: "horizontal",
// // //       slidesPerView: 3.5,
// // //       spaceBetween: 8,
// // //       threshold: 5,
// // //       watchOverflow: true,
// // //       watchSlidesVisibility: true,
// // //       watchSlidesProgress: true,
// // //       breakpoints: {
// // //         750: {
// // //           direction: swiperElement.dataset.thumbsDirectionDesktop || "vertical",
// // //           spaceBetween: 8,
// // //           slidesPerView: 3.5,
// // //         },
// // //       },
// // //     });

// // //     this.settings.options.thumbs = { swiper: thumbSwiper };

// // //     this.settings.options.on.slideChange = (swiper) => {
// // //       const currentIndex = swiper.realIndex;
// // //       if (currentIndex >= 3) {
// // //         thumbSwiper.slideTo(currentIndex - 2);
// // //       } else {
// // //         thumbSwiper.slideTo(0);
// // //       }
// // //     };
// // //   }

// // //   connectedCallback() {
// // //     this.init();
// // //     this.attachArrowEvents();
// // //   }

// // //   init() {
// // //     if (!this.settings.sliderElement) return;
// // //     this.settings.sliderInstance = new Swiper(
// // //       this.settings.sliderElement,
// // //       this.settings.options
// // //     );
// // //     this.swiperInstance = this.settings.sliderInstance;
// // //   }

// // //   attachArrowEvents() {
// // //     const prevBtn = this.querySelector(".swiper-button-prev");
// // //     const nextBtn = this.querySelector(".swiper-button-next");
// // //     if (prevBtn && nextBtn && this.settings.sliderInstance) {
// // //       prevBtn.addEventListener("click", () => {
// // //         this.settings.sliderInstance.slidePrev();
// // //       });
// // //       nextBtn.addEventListener("click", () => {
// // //         this.settings.sliderInstance.slideNext();
// // //       });
// // //     }
// // //   }

// // //   setActiveModalOpener(swiper) {
// // //     const activeSlide = swiper.slides[swiper.activeIndex];
// // //     if (!activeSlide) return;
// // //     const activeMediaId = activeSlide.dataset.mediaId;
// // //     this.querySelector(
// // //       `${this.selectors.modalOpeners}.is-active`
// // //     )?.classList.remove("is-active");
// // //     this.querySelector(
// // //       `${this.selectors.modalOpeners} [data-media-id="${activeMediaId}"]`
// // //     )?.parentElement.classList.add("is-active");
// // //     if (activeSlide.dataset.mediaType === "model") {
// // //       this.querySelector(this.selectors.btnXr).dataset.shopifyModel3dId =
// // //         activeMediaId;
// // //     }
// // //   }
// // // }

// // // customElements.define("product-media", ProductMedia);

// // // class ProductMedia extends HTMLElement {
// // //   constructor() {
// // //     super();

// // //     this.selectors = {
// // //       slider: "[data-slider]",
// // //       mediaItem: "[data-media-id]",
// // //       modalOpeners: "[data-modal-opener-id]",
// // //       zoomImages: "[data-pswp-image]",
// // //       btnXr: "[data-shopify-xr]",
// // //     };
// // //     this.selectedMediaIndex =
// // //       Number(
// // //         this.querySelector(this.selectors.slider).querySelector(
// // //           "[data-selected]"
// // //         )?.dataset.index
// // //       ) || 0;

// // //     const swiperElement = document.getElementById("product__media-slider");
// // //     let swiperOptions = {};
// // //     try {
// // //       const optionsString = this.querySelector(
// // //         this.selectors.slider
// // //       ).getAttribute("data-swiper-options");
// // //       swiperOptions = optionsString ? JSON.parse(optionsString) : {};
// // //     } catch (error) {
// // //       console.error("Invalid JSON in data-swiper-options:", error);
// // //     }

// // //     const withThumbs = this.dataset.withThumbs === "true";
// // //     const hasCustomThumbnails = window.innerWidth >= 750; // Use custom thumbnails on desktop

// // //     this.settings = {
// // //       sliderElement: this.querySelector(this.selectors.slider),
// // //       sliderInstance: null,
// // //       options: {
// // //         ...swiperOptions,
// // //         initialSlide: this.selectedMediaIndex,
// // //         navigation: {
// // //           prevEl: this.querySelector(".swiper-button-prev"),
// // //           nextEl: this.querySelector(".swiper-button-next"),
// // //         },
// // //         pagination: {
// // //           el: ".swiper-pagination",
// // //           type: "progressbar",
// // //         },
// // //         spaceBetween: 2,
// // //         watchOverflow: true,
// // //         watchSlidesVisibility: true,
// // //         watchSlidesProgress: true,
// // //         preventInteractionOnTransition: false,
// // //         autoHeight: this.dataset.autoHeight === "true",
// // //         loop: true,
// // //         allowTouchMove: !hasCustomThumbnails, // Disable touch on desktop when using custom thumbnails
// // //         speed: 0, // Instant swap for main image
// // //         effect: "fade",
// // //         fadeEffect: {
// // //           crossFade: true,
// // //         },
// // //         on: {
// // //           afterInit: (swiper) => this.afterInit(swiper),
// // //           slideChangeTransitionEnd: (swiper) =>
// // //             this.setActiveModalOpener(swiper),
// // //           transitionStart: (swiper) => this.pauseAllVideos(swiper),
// // //           transitionEnd: (swiper) => this.playActiveSlideVideo(swiper),
// // //         },
// // //       },
// // //     };

// // //     // Use custom thumbnails on desktop, regular swiper thumbnails on mobile
// // //     if (withThumbs && !hasCustomThumbnails) {
// // //       this.initThumbnails(swiperElement);
// // //     } else {
// // //       this.settings.options.slidesPerView = 1;
// // //     }
// // //   }

// // //   afterInit(swiper) {
// // //     if (swiper.slides.length > 1) {
// // //       this.playActiveSlideVideo(swiper);
// // //     }
// // //   }

// // //   pauseAllVideos(swiper) {
// // //     swiper.el.querySelectorAll("video").forEach((video) => video.pause());
// // //   }

// // //   playActiveSlideVideo(swiper) {
// // //     const activeSlide = swiper.slides[swiper.activeIndex];
// // //     if (!activeSlide) return;
// // //     const video = activeSlide.querySelector("video");
// // //     if (video) this.playPromise(video);
// // //   }

// // //   playPromise(video) {
// // //     let playPromise = video.play();
// // //     if (playPromise !== undefined) {
// // //       playPromise.catch(() => video.pause());
// // //     }
// // //   }

// // //   initThumbnails(swiperElement) {
// // //     // Only initialize swiper thumbnails on mobile
// // //     const thumbSwiper = new Swiper(".swiper-thumbs", {
// // //       initialSlide: this.selectedMediaIndex,
// // //       centeredSlides: false,
// // //       centeredSlidesBounds: true,
// // //       freeMode: false,
// // //       direction: "horizontal",
// // //       slidesPerView: 3.5,
// // //       spaceBetween: 8,
// // //       threshold: 5,
// // //       watchOverflow: true,
// // //       watchSlidesVisibility: true,
// // //       watchSlidesProgress: true,
// // //       loop: true,
// // //     });

// // //     this.settings.options.thumbs = { swiper: thumbSwiper };

// // //     this.settings.options.on.slideChange = (swiper) => {
// // //       const currentIndex = swiper.realIndex;
// // //       thumbSwiper.slideTo(currentIndex);
// // //     };
// // //   }

// // //   connectedCallback() {
// // //     this.init();
// // //     this.attachArrowEvents();
// // //   }

// // //   init() {
// // //     if (!this.settings.sliderElement) return;
// // //     this.settings.sliderInstance = new Swiper(
// // //       this.settings.sliderElement,
// // //       this.settings.options
// // //     );
// // //     this.swiperInstance = this.settings.sliderInstance;
// // //   }

// // //   attachArrowEvents() {
// // //     const prevBtn = this.querySelector(".swiper-button-prev");
// // //     const nextBtn = this.querySelector(".swiper-button-next");
// // //     if (prevBtn && nextBtn && this.settings.sliderInstance) {
// // //       prevBtn.addEventListener("click", () => {
// // //         this.settings.sliderInstance.slidePrev();
// // //       });
// // //       nextBtn.addEventListener("click", () => {
// // //         this.settings.sliderInstance.slideNext();
// // //       });
// // //     }
// // //   }

// // //   setActiveModalOpener(swiper) {
// // //     const activeSlide = swiper.slides[swiper.activeIndex];
// // //     if (!activeSlide) return;
// // //     const activeMediaId = activeSlide.dataset.mediaId;
// // //     this.querySelector(
// // //       `${this.selectors.modalOpeners}.is-active`
// // //     )?.classList.remove("is-active");
// // //     this.querySelector(
// // //       `${this.selectors.modalOpeners} [data-media-id="${activeMediaId}"]`
// // //     )?.parentElement.classList.add("is-active");
// // //     if (activeSlide.dataset.mediaType === "model") {
// // //       this.querySelector(this.selectors.btnXr).dataset.shopifyModel3dId =
// // //         activeMediaId;
// // //     }
// // //   }
// // // }

// // // customElements.define("product-media", ProductMedia);

// // // class ProductMedia extends HTMLElement {
// // //   constructor() {
// // //     super();

// // //     this.selectors = {
// // //       slider: "[data-slider]",
// // //       mediaItem: "[data-media-id]",
// // //       modalOpeners: "[data-modal-opener-id]",
// // //       zoomImages: "[data-pswp-image]",
// // //       btnXr: "[data-shopify-xr]",
// // //     };
// // //     this.selectedMediaIndex =
// // //       Number(
// // //         this.querySelector(this.selectors.slider).querySelector(
// // //           "[data-selected]"
// // //         )?.dataset.index
// // //       ) || 0;

// // //     const swiperElement = document.getElementById("product__media-slider");
// // //     let swiperOptions = {};
// // //     try {
// // //       const optionsString = this.querySelector(
// // //         this.selectors.slider
// // //       ).getAttribute("data-swiper-options");
// // //       swiperOptions = optionsString ? JSON.parse(optionsString) : {};
// // //     } catch (error) {
// // //       console.error("Invalid JSON in data-swiper-options:", error);
// // //     }

// // //     const withThumbs = this.dataset.withThumbs === "true";
// // //     const hasCustomThumbnails = window.innerWidth >= 750; // Use custom thumbnails on desktop

// // //     this.settings = {
// // //       sliderElement: this.querySelector(this.selectors.slider),
// // //       sliderInstance: null,
// // //       options: {
// // //         ...swiperOptions,
// // //         initialSlide: this.selectedMediaIndex,
// // //         navigation: {
// // //           prevEl: this.querySelector(".swiper-button-prev"),
// // //           nextEl: this.querySelector(".swiper-button-next"),
// // //         },
// // //         pagination: {
// // //           el: ".swiper-pagination",
// // //           type: "progressbar",
// // //         },
// // //         spaceBetween: 2,
// // //         watchOverflow: true,
// // //         watchSlidesVisibility: true,
// // //         watchSlidesProgress: true,
// // //         preventInteractionOnTransition: false,
// // //         autoHeight: this.dataset.autoHeight === "true",
// // //         loop: true,
// // //         allowTouchMove: !hasCustomThumbnails, // Disable touch on desktop when using custom thumbnails
// // //         speed: 0, // Instant swap for main image
// // //         effect: 'fade',
// // //         fadeEffect: {
// // //           crossFade: true
// // //         },
// // //         on: {
// // //           afterInit: (swiper) => this.afterInit(swiper),
// // //           slideChangeTransitionEnd: (swiper) =>
// // //             this.setActiveModalOpener(swiper),
// // //           transitionStart: (swiper) => this.pauseAllVideos(swiper),
// // //           transitionEnd: (swiper) => this.playActiveSlideVideo(swiper),
// // //         },
// // //       },
// // //     };

// // //     // Don't initialize swiper thumbnails on desktop when using custom thumbnails
// // //     if (withThumbs && !hasCustomThumbnails) {
// // //       this.initThumbnails(swiperElement);
// // //     } else {
// // //       this.settings.options.slidesPerView = 1;
// // //       // Remove thumbs from options to prevent Swiper from trying to control them
// // //       if (hasCustomThumbnails) {
// // //         delete this.settings.options.thumbs;
// // //       }
// // //     }
// // //   }

// // //   afterInit(swiper) {
// // //     if (swiper.slides.length > 1) {
// // //       this.playActiveSlideVideo(swiper);
// // //     }
// // //   }

// // //   pauseAllVideos(swiper) {
// // //     swiper.el.querySelectorAll("video").forEach((video) => video.pause());
// // //   }

// // //   playActiveSlideVideo(swiper) {
// // //     const activeSlide = swiper.slides[swiper.activeIndex];
// // //     if (!activeSlide) return;
// // //     const video = activeSlide.querySelector("video");
// // //     if (video) this.playPromise(video);
// // //   }

// // //   playPromise(video) {
// // //     let playPromise = video.play();
// // //     if (playPromise !== undefined) {
// // //       playPromise.catch(() => video.pause());
// // //     }
// // //   }

// // //   initThumbnails(swiperElement) {
// // //     // Only initialize swiper thumbnails on mobile
// // //     const thumbSwiper = new Swiper(".swiper-thumbs", {
// // //       initialSlide: this.selectedMediaIndex,
// // //       centeredSlides: false,
// // //       centeredSlidesBounds: true,
// // //       freeMode: false,
// // //       direction: "horizontal",
// // //       slidesPerView: 3.5,
// // //       spaceBetween: 8,
// // //       threshold: 5,
// // //       watchOverflow: true,
// // //       watchSlidesVisibility: true,
// // //       watchSlidesProgress: true,
// // //       loop: true,
// // //     });

// // //     this.settings.options.thumbs = { swiper: thumbSwiper };

// // //     this.settings.options.on.slideChange = (swiper) => {
// // //       const currentIndex = swiper.realIndex;
// // //       thumbSwiper.slideTo(currentIndex);
// // //     };
// // //   }

// // //   connectedCallback() {
// // //     this.init();
// // //     this.attachArrowEvents();
// // //   }

// // //   init() {
// // //     if (!this.settings.sliderElement) return;
// // //     this.settings.sliderInstance = new Swiper(
// // //       this.settings.sliderElement,
// // //       this.settings.options
// // //     );
// // //     this.swiperInstance = this.settings.sliderInstance;
// // //   }

// // //   attachArrowEvents() {
// // //     const prevBtn = this.querySelector(".swiper-button-prev");
// // //     const nextBtn = this.querySelector(".swiper-button-next");
// // //     if (prevBtn && nextBtn && this.settings.sliderInstance) {
// // //       prevBtn.addEventListener("click", () => {
// // //         this.settings.sliderInstance.slidePrev();
// // //       });
// // //       nextBtn.addEventListener("click", () => {
// // //         this.settings.sliderInstance.slideNext();
// // //       });
// // //     }
// // //   }

// // //   setActiveModalOpener(swiper) {
// // //     const activeSlide = swiper.slides[swiper.activeIndex];
// // //     if (!activeSlide) return;
// // //     const activeMediaId = activeSlide.dataset.mediaId;
// // //     this.querySelector(
// // //       `${this.selectors.modalOpeners}.is-active`
// // //     )?.classList.remove("is-active");
// // //     this.querySelector(
// // //       `${this.selectors.modalOpeners} [data-media-id="${activeMediaId}"]`
// // //     )?.parentElement.classList.add("is-active");
// // //     if (activeSlide.dataset.mediaType === "model") {
// // //       this.querySelector(this.selectors.btnXr).dataset.shopifyModel3dId =
// // //         activeMediaId;
// // //     }
// // //   }
// // // }

// // // customElements.define("product-media", ProductMedia);

// // class ProductMedia extends HTMLElement {
// //   constructor() {
// //     super();

// //     this.selectors = {
// //       slider: "[data-slider]",
// //       mediaItem: "[data-media-id]",
// //       modalOpeners: "[data-modal-opener-id]",
// //       zoomImages: "[data-pswp-image]",
// //       btnXr: "[data-shopify-xr]",
// //     };
// //     this.selectedMediaIndex =
// //       Number(
// //         this.querySelector(this.selectors.slider).querySelector(
// //           "[data-selected]"
// //         )?.dataset.index
// //       ) || 0;

// //     const swiperElement = document.getElementById("product__media-slider");
// //     let swiperOptions = {};
// //     try {
// //       const optionsString = this.querySelector(
// //         this.selectors.slider
// //       ).getAttribute("data-swiper-options");
// //       swiperOptions = optionsString ? JSON.parse(optionsString) : {};
// //     } catch (error) {
// //       console.error("Invalid JSON in data-swiper-options:", error);
// //     }

// //     const withThumbs = this.dataset.withThumbs === "true";
// //     const hasCustomThumbnails = window.innerWidth >= 750; // Use custom thumbnails on desktop

// //     this.settings = {
// //       sliderElement: this.querySelector(this.selectors.slider),
// //       sliderInstance: null,
// //       options: {
// //         ...swiperOptions,
// //         initialSlide: this.selectedMediaIndex,
// //         navigation: {
// //           prevEl: this.querySelector(".swiper-button-prev"),
// //           nextEl: this.querySelector(".swiper-button-next"),
// //         },
// //         pagination: {
// //           el: ".swiper-pagination",
// //           type: "progressbar",
// //         },
// //         spaceBetween: 2,
// //         watchOverflow: true,
// //         watchSlidesVisibility: true,
// //         watchSlidesProgress: true,
// //         preventInteractionOnTransition: false,
// //         autoHeight: this.dataset.autoHeight === "true",
// //         loop: true,
// //         allowTouchMove: !hasCustomThumbnails, // Disable touch on desktop when using custom thumbnails
// //         speed: 0, // Instant swap for main image
// //         effect: "fade",
// //         fadeEffect: {
// //           crossFade: true,
// //         },
// //         on: {
// //           afterInit: (swiper) => this.afterInit(swiper),
// //           slideChangeTransitionEnd: (swiper) =>
// //             this.setActiveModalOpener(swiper),
// //           transitionStart: (swiper) => this.pauseAllVideos(swiper),
// //           transitionEnd: (swiper) => this.playActiveSlideVideo(swiper),
// //         },
// //       },
// //     };

// //     // Initialize thumbnails normally, but they'll be overridden by CustomThumbnails on desktop
// //     if (withThumbs) {
// //       this.initThumbnails(swiperElement);
// //     } else {
// //       this.settings.options.slidesPerView = 1;
// //     }
// //   }

// //   afterInit(swiper) {
// //     if (swiper.slides.length > 1) {
// //       this.playActiveSlideVideo(swiper);
// //     }
// //   }

// //   pauseAllVideos(swiper) {
// //     swiper.el.querySelectorAll("video").forEach((video) => video.pause());
// //   }

// //   playActiveSlideVideo(swiper) {
// //     const activeSlide = swiper.slides[swiper.activeIndex];
// //     if (!activeSlide) return;
// //     const video = activeSlide.querySelector("video");
// //     if (video) this.playPromise(video);
// //   }

// //   playPromise(video) {
// //     let playPromise = video.play();
// //     if (playPromise !== undefined) {
// //       playPromise.catch(() => video.pause());
// //     }
// //   }

// //   initThumbnails(swiperElement) {
// //     // Only initialize swiper thumbnails on mobile
// //     const thumbSwiper = new Swiper(".swiper-thumbs", {
// //       initialSlide: this.selectedMediaIndex,
// //       centeredSlides: false,
// //       centeredSlidesBounds: true,
// //       freeMode: false,
// //       direction: "horizontal",
// //       slidesPerView: 3.5,
// //       spaceBetween: 8,
// //       threshold: 5,
// //       watchOverflow: true,
// //       watchSlidesVisibility: true,
// //       watchSlidesProgress: true,
// //       loop: true,
// //     });

// //     this.settings.options.thumbs = { swiper: thumbSwiper };

// //     this.settings.options.on.slideChange = (swiper) => {
// //       const currentIndex = swiper.realIndex;
// //       thumbSwiper.slideTo(currentIndex);
// //     };
// //   }

// //   connectedCallback() {
// //     this.init();
// //     this.attachArrowEvents();
// //   }

// //   init() {
// //     if (!this.settings.sliderElement) return;
// //     this.settings.sliderInstance = new Swiper(
// //       this.settings.sliderElement,
// //       this.settings.options
// //     );
// //     this.swiperInstance = this.settings.sliderInstance;
// //   }

// //   attachArrowEvents() {
// //     const prevBtn = this.querySelector(".swiper-button-prev");
// //     const nextBtn = this.querySelector(".swiper-button-next");
// //     if (prevBtn && nextBtn && this.settings.sliderInstance) {
// //       prevBtn.addEventListener("click", () => {
// //         this.settings.sliderInstance.slidePrev();
// //       });
// //       nextBtn.addEventListener("click", () => {
// //         this.settings.sliderInstance.slideNext();
// //       });
// //     }
// //   }

// //   setActiveModalOpener(swiper) {
// //     const activeSlide = swiper.slides[swiper.activeIndex];
// //     if (!activeSlide) return;
// //     const activeMediaId = activeSlide.dataset.mediaId;
// //     this.querySelector(
// //       `${this.selectors.modalOpeners}.is-active`
// //     )?.classList.remove("is-active");
// //     this.querySelector(
// //       `${this.selectors.modalOpeners} [data-media-id="${activeMediaId}"]`
// //     )?.parentElement.classList.add("is-active");
// //     if (activeSlide.dataset.mediaType === "model") {
// //       this.querySelector(this.selectors.btnXr).dataset.shopifyModel3dId =
// //         activeMediaId;
// //     }
// //   }
// // }

// // customElements.define("product-media", ProductMedia);
